import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useRoute, useLocation, Link } from "wouter";
import { useCreateOrder, customFetch, customerCustomFetch } from "@workspace/api-client-react";
import { EncomendaDoPedido } from "@/components/catalog/EncomendaDoPedido";
import { dataUnicaDosItens, itensDeEncomenda, modoDoCarrinho } from "@/lib/encomendaUnica";
import type { CreateOrderRequest, CatalogResponse } from "@workspace/api-client-react";
import { useQuery } from "@tanstack/react-query";
import { getCartStore, quantityPromoCartDiscounts } from "@/hooks/use-cart";
import { promocaoNasOpcoes, quantityPromoUnitDiscountsByLine } from "@/lib/promotion";
import { useCustomerAuth } from "@/hooks/use-customer-auth";
import { formatCurrency } from "@/lib/utils";
import { useStoreColors } from "@/hooks/use-store-colors";
import { useCatalogRealtime } from "@/hooks/use-catalog-realtime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BirthDateInput } from "@/components/ui/birth-date-input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { setStoreTimezone, getStoreTimezone } from "@/lib/store-timezone";
import { formatBRDate, formatBRTime } from "@/lib/datetime";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ChevronLeft, MapPin, CreditCard, Loader2, Truck, Bell, Store, AlertTriangle, Search, Pencil, Plus, Trash2, Check, X, User, TicketPercent, MessageSquareText, Gift } from "lucide-react";
import { AddressAutocomplete, type AddressData } from "@/components/AddressAutocomplete";
import { ClosedStoreCard } from "@/components/ClosedStoreCard";
import { StoreInactiveCard } from "@/components/StoreInactiveCard";
import { CustomerLoginModal } from "@/components/CustomerLoginModal";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { extractApiErrorMessage } from "@/lib/api-error";
import { errorTitle, TRY_AGAIN_DESCRIPTION } from "@/lib/messages";
import { subscribeCustomerPush } from "@/hooks/use-push-notifications";
import { computeCardSurcharge, computeOrderTotal, computeCouponMath, computeRewardMath, normalizeLoyaltyConfig, pointsForOrder, rewardProblem, encomendaPorExtenso, horarioDosTurnos, entregaPorBairroSemSaida, modoDeEntregaInicial, bairroDaTaxaEscolhido, prepCountFor, prepCountLine, gruposComConta, type RewardUse, type Turno } from "@workspace/pricing";
import { PontosDoPedido, type PremioEscolhido } from "@/components/catalog/PontosDoPedido";
import { ConfirmNumberDialog } from "@/components/CustomerCodeStep";
import { chaveIndicacao, chavePremio, diaDaSemanaNaLoja, type StoreLoyalty } from "@/lib/loyalty";
import { groupOptionsByTitle } from "@workspace/escpos";
import { BrandTile } from "@/components/BrandTile";
import { SeletorDeBairro } from "@/components/catalog/SeletorDeBairro";
import { normalizeNeighborhood } from "@/lib/bairro";

function getApiBase(): string {
  // Em prod com subdomínio split (api.vendanozap.app), VITE_API_URL é injetado
  // no build. Em dev (proxy Vite -> localhost:8080), VITE_API_URL é vazio e
  // chamadas relativas /api/* batem no proxy.
  const fromEnv = (import.meta.env.VITE_API_URL as string | undefined)?.trim();
  if (fromEnv) return fromEnv.replace(/\/+$/, "");
  return (import.meta.env.BASE_URL || "/").replace(/\/+$/, "");
}

interface DeliveryFeeResult {
  fee: number | null;
  distanceKm: number | null;
  noDelivery: boolean;
  neighborhood?: string | null;
  neighborhoods?: Array<{ name: string; fee: number }>;
  acceptUnlisted?: boolean;
  unlistedFee?: number | null;
  /** Frete a fechar depois pelo lojista. `fee` null aqui é "a calcular", não
   * "grátis" — quem exibe total tem que olhar isto. */
  feePending?: boolean;
  unlistedMode?: string;
}

type ManualAddressFieldKey =
  | "cep"
  | "state"
  | "city"
  | "neighborhood"
  | "street"
  | "number"
  | "complement"
  | "reference";

const MANUAL_ADDRESS_FIELD_ORDER: ManualAddressFieldKey[] = [
  "cep",
  "state",
  "city",
  "neighborhood",
  "street",
  "number",
  "complement",
  "reference",
];

const MANUAL_ADDRESS_FIELD_LABELS: Record<ManualAddressFieldKey, string> = {
  cep: "CEP",
  state: "Estado",
  city: "Cidade",
  neighborhood: "Bairro",
  street: "Rua",
  number: "Número",
  complement: "Complemento",
  reference: "Ponto de referência",
};

const MANUAL_ADDRESS_FIELD_PLACEHOLDERS: Record<ManualAddressFieldKey, string> = {
  cep: "CEP",
  state: "Estado (UF)",
  city: "Cidade",
  neighborhood: "Bairro",
  street: "Rua / Avenida",
  number: "Número",
  complement: "Complemento (apto, bloco)",
  reference: "Ponto de referência (próximo a...)",
};

const MANUAL_ADDRESS_FIELD_DEFAULTS: Record<ManualAddressFieldKey, { enabled: boolean; required: boolean }> = {
  cep:          { enabled: false, required: false },
  state:        { enabled: false, required: false },
  city:         { enabled: false, required: false },
  neighborhood: { enabled: true,  required: true },
  street:       { enabled: true,  required: true },
  number:       { enabled: true,  required: true },
  complement:   { enabled: true,  required: false },
  reference:    { enabled: false, required: false },
};

const MANUAL_ADDRESS_ADDRESS_KEY: Record<ManualAddressFieldKey, string> = {
  cep: "zipCode",
  state: "state",
  city: "city",
  neighborhood: "neighborhood",
  street: "street",
  number: "number",
  complement: "complement",
  reference: "reference",
};

function resolveManualAddressFields(raw: any): Record<ManualAddressFieldKey, { enabled: boolean; required: boolean }> {
  const out = { ...MANUAL_ADDRESS_FIELD_DEFAULTS };
  if (raw && typeof raw === "object") {
    for (const k of MANUAL_ADDRESS_FIELD_ORDER) {
      const v = raw[k];
      if (v && typeof v === "object") {
        const enabled = !!v.enabled;
        out[k] = { enabled, required: enabled && !!v.required };
      }
    }
  }
  return out;
}

export default function Checkout() {
  const confirm = useConfirm();
  const [, params] = useRoute("/loja/:slug/checkout");
  const slug = params?.slug || "";
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const unitId = new URLSearchParams(window.location.search).get("unitId");
  const cartKey = unitId ? `${slug}:${unitId}` : slug;
  
  // Sino do catálogo: além do refetch, força a revalidação do cupom aplicado
  // (lojista pode ter desativado o cupom com o cliente no checkout).
  const [catalogBump, setCatalogBump] = useState(0);
  useCatalogRealtime(slug, () => setCatalogBump((b) => b + 1));

  const { data: catalog, isLoading: loadingCatalog } = useQuery<CatalogResponse>({
    queryKey: ["catalog", slug, unitId],
    queryFn: () => {
      const url = unitId
        ? `/api/catalog/${slug}?unitId=${unitId}`
        : `/api/catalog/${slug}`;
      return customFetch(url);
    },
    enabled: !!slug,
  });
  const store = catalog?.store;

  // Relógio da loja pros helpers de promoção/data.
  useEffect(() => {
    setStoreTimezone((store as { timezone?: string | null } | undefined)?.timezone ?? null);
  }, [store]);

  // Loja fechada + toggle "receber pedidos com a loja fechada" desligado →
  // pedido bloqueado (o servidor também rejeita; aqui é o aviso/guard da UI).
  const closedAndBlocked =
    catalog?.isOpen === false &&
    (store as { acceptOrdersWhenClosed?: boolean } | undefined)?.acceptOrdersWhenClosed === false;

  // Botão "aberta/fechada" do painel desligado: o POST de pedido é recusado
  // pelo servidor de qualquer jeito, então o checkout trava na entrada em vez
  // de deixar preencher tudo pra levar o não no fim.
  const lojaDesativada = (catalog as { isActive?: boolean } | undefined)?.isActive === false;

  const useCart = getCartStore(cartKey);
  const items = useCart(state => state.items);
  // Data única no carrinho (loja com orderSingleDate, 05/10/2026): uma data e um horário para o pedido inteiro,
  // escolhidos aqui; vão para cada item por encomenda no envio. A pronta-entrega sai junto (decisão do Pedro).
  const [encData, setEncData] = useState<string | null>(null);
  const [encHora, setEncHora] = useState<string | null>(null);
  // Turno da data única (12/10/2026): quando o produto mais detalhado do carrinho pede turno.
  const [encTurno, setEncTurno] = useState<Turno | null>(null);
  const dataUnica = (store as { orderSingleDate?: boolean } | undefined)?.orderSingleDate === true;
  const itensEnc = useMemo(() => (dataUnica ? itensDeEncomenda(items) : []), [dataUnica, items]);
  const precisaEncomenda = itensEnc.length > 0;
  const modoEnc = modoDoCarrinho(itensEnc);
  const horariosDosTurnos = useMemo(() => horarioDosTurnos((store as { orderShiftHours?: unknown } | undefined)?.orderShiftHours, (store as { businessHours?: unknown } | undefined)?.businessHours), [store]);
  const encomendaCompleta = !!encData && (modoEnc === "horario" ? !!encHora : modoEnc === "turno" ? !!encTurno : true);
  // Data/hora/turno que vão no item: a do pedido (data única, no modo mais detalhado do carrinho) ou a escolhida no
  // produto (data por item).
  const agendaDoItem = (i: { product: any; scheduledDate?: string; scheduledTime?: string; scheduledShift?: string }) =>
    precisaEncomenda && i.product?.isOrderBased
      ? {
          scheduledDate: encData ?? undefined,
          scheduledTime: modoEnc === "horario" ? encHora ?? undefined : undefined,
          scheduledShift: modoEnc === "turno" ? encTurno ?? undefined : undefined,
        }
      : { scheduledDate: i.scheduledDate, scheduledTime: i.scheduledTime, scheduledShift: i.scheduledShift };
  const total = useCart(state => state.getTotal());
  // Promo por quantidade: `total` já vem líquido (mesma conta do servidor).
  // Pra leitura, o Subtotal exibido é o BRUTO e a promoção aparece como linha
  // própria — igual ao cupom.
  const qtyPromoEntries = quantityPromoCartDiscounts(items);
  const qtyPromoTotal = qtyPromoEntries.reduce((s, e) => s + e.discount, 0);
  const grossSubtotal = Math.round((total + qtyPromoTotal) * 100) / 100;
  const clearCart = useCart(state => state.clearCart);

  const { customer, isLoading: customerLoading, verified: customerVerified } = useCustomerAuth();
  const [loginOpen, setLoginOpen] = useState(false);

  const [deliveryMode, setDeliveryMode] = useState<string>("");
  // Forma de retirada (null = na loja). Só vale com deliveryMode "retirada".
  const [pickupMethod, setPickupMethod] = useState<string | null>(null);
  useEffect(() => {
    if (deliveryMode !== "retirada" && pickupMethod) setPickupMethod(null);
  }, [deliveryMode, pickupMethod]);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  // Pagamento misto (duas formas): cliente digita o valor da 1ª forma e a 2ª
  // recebe o restante automaticamente (mesma semântica do PDV).
  const [paymentMixed, setPaymentMixed] = useState(false);
  const [mixedMethod1, setMixedMethod1] = useState("");
  const [mixedMethod2, setMixedMethod2] = useState("");
  const [mixedAmount1, setMixedAmount1] = useState(0);

  // Misto é opt-in do lojista: se a loja não permite (ou desligou no meio do
  // caminho), volta pro fluxo de forma única.
  useEffect(() => {
    if (paymentMixed && (catalog?.store as { allowMixedPayments?: boolean } | undefined)?.allowMixedPayments !== true) {
      setPaymentMixed(false);
    }
  }, [paymentMixed, catalog?.store]);
  const [birthDate, setBirthDate] = useState<string>("");

  // Cupom de desconto: valida no servidor (mensagens específicas de erro) e
  // guarda tipo+valor pra recomputar o desconto localmente sobre o subtotal.
  const [couponInput, setCouponInput] = useState("");
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponChecking, setCouponChecking] = useState(false);
  // Cupom escolhido na FAIXA da vitrine ("usar"): chega pronto e aplica
  // sozinho uma vez; o checkCoupon continua sendo o juiz.
  const cupomDaVitrine = useRef(false);
  useEffect(() => {
    if (cupomDaVitrine.current) return;
    let raw: string | null = null;
    try { raw = localStorage.getItem(`vnz-cupom-${slug}`); } catch {}
    if (raw && appliedCoupons.length === 0) {
      cupomDaVitrine.current = true;
      let codes: string[] = [];
      try { const j = JSON.parse(raw); codes = Array.isArray(j) ? j : [raw]; } catch { codes = [raw]; }
      for (const code of codes) applyCouponRef.current?.(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  const applyCouponRef = useRef<((code?: string) => void) | null>(null);
  // Cumulativo (stores.stackable_coupons): lista; loja comum fica em 0..1.
  const [appliedCoupons, setAppliedCoupons] = useState<Array<{ code: string; discountType: "percent" | "fixed" | "free_delivery"; discountValue: number }>>([]);
  const stackable = (store as any)?.stackableCoupons === true;
  const setAppliedCoupon = (c: { code: string; discountType: "percent" | "fixed"; discountValue: number } | null) =>
    setAppliedCoupons(c ? [c] : []);
  const [needsChange, setNeedsChange] = useState<boolean | null>(null);
  const [changeFor, setChangeFor] = useState<string>("");
  // Pedido para presente (Ultra, 22/10/2026): a loja pergunta; com Sim, quem vai receber vai no pedido, abaixo do cliente.
  const pedePresente = (store as { askGiftRecipient?: boolean } | undefined)?.askGiftRecipient === true;
  const presenteObrigatorio = pedePresente && (store as { giftRecipientRequired?: boolean } | undefined)?.giftRecipientRequired === true;
  const [ehPresente, setEhPresente] = useState<boolean | null>(null);
  const [quemRecebe, setQuemRecebe] = useState("");

  // Programa de pontos (Ultra, 05/10/2026): saldo e prêmios da loja. Troca só com o número confirmado.
  const loyaltyOn = (store as { loyaltyEnabled?: boolean } | undefined)?.loyaltyEnabled === true;
  const { data: loyalty, refetch: refetchLoyalty } = useQuery<StoreLoyalty>({
    queryKey: ["store-loyalty", slug, customer?.id ?? null, customerVerified],
    queryFn: () => customerCustomFetch<StoreLoyalty>(`/api/catalog/${slug}/loyalty`),
    enabled: loyaltyOn && !!customer && !!slug,
    staleTime: 30_000,
  });
  const [premiosEscolhidos, setPremiosEscolhidos] = useState<PremioEscolhido[]>([]);
  const [confirmarNumero, setConfirmarNumero] = useState(false);
  // Indicação: o link guardou o ?ind= de quem indicou (vale só no 1º pedido — o servidor confere).
  const indicacao = useMemo(() => {
    try {
      return localStorage.getItem(chaveIndicacao(slug)) || null;
    } catch {
      return null;
    }
  }, [slug]);
  // "Usar" em Minha conta: o prêmio chega escolhido.
  useEffect(() => {
    try {
      const id = localStorage.getItem(chavePremio(slug));
      if (id) {
        setPremiosEscolhidos([{ rewardId: id }]);
        localStorage.removeItem(chavePremio(slug));
      }
    } catch {}
  }, [slug]);

  type HighlightField =
    | "encomenda"
    | "delivery"
    | "address"
    | "addressForm"
    | "noDelivery"
    | "feeLoading"
    | "payment"
    | "changeFor"
    | "gift"
    | "giftName"
    | null;
  const [highlightField, setHighlightField] = useState<HighlightField>(null);
  const deliveryCardRef = useRef<HTMLDivElement>(null);
  const encomendaCardRef = useRef<HTMLDivElement>(null);
  const addressBlockRef = useRef<HTMLDivElement>(null);
  const minimoDoBairroRef = useRef<HTMLDivElement>(null);
  const paymentCardRef = useRef<HTMLDivElement>(null);
  const changeForInputRef = useRef<HTMLInputElement>(null);
  const giftCardRef = useRef<HTMLDivElement>(null);
  const giftNameInputRef = useRef<HTMLInputElement>(null);
  const [notes, setNotes] = useState("");
  const [address, setAddress] = useState({ street: "", number: "", complement: "", neighborhood: "", city: "", state: "", zipCode: "", reference: "", lat: null as number | null, lng: null as number | null });
  const [googleMapsKey, setGoogleMapsKey] = useState<string | null>(null);

  type SavedAddress = {
    id: string;
    street: string;
    number: string;
    complement: string | null;
    neighborhood: string;
    city: string;
    state: string | null;
    zipCode: string;
    reference: string | null;
    lat: number | null;
    lng: number | null;
    label: string | null;
    isDefault: boolean;
  };
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");
  const [addressFormMode, setAddressFormMode] = useState<"hidden" | "new" | "edit">("hidden");
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressLabel, setAddressLabel] = useState("Casa");
  // A busca do Google devolve `street_number` só quando o cliente escolhe um
  // endereço COM número. Quem digita "R. X, n 748" casa a rua e volta sem
  // número — e aí rua, complemento e CEP preenchidos fazem o formulário
  // PARECER completo com o Nº vazio. Não bloqueia (10,2% dos pedidos já chegam
  // sem número hoje, barrar isso são centenas), só deixa impossível não ver.
  // Campos obrigatórios apontados no clique de enviar: destaque âmbar até
  // preencher (a classe testa fieldFilled, então limpa sozinha ao digitar).
  const [flagMissing, setFlagMissing] = useState<ManualAddressFieldKey[]>([]);
  const [userManualEntry, setUserManualEntry] = useState(false);
  const initialAddressLoadRef = useRef(false);

  const [dynamicFee, setDynamicFee] = useState<DeliveryFeeResult | null>(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState("");
  const [neighborhoodList, setNeighborhoodList] = useState<Array<{ name: string; fee: number; minOrder: number }>>([]);
  const [neighborhoodMeta, setNeighborhoodMeta] = useState<{ acceptUnlisted: boolean; unlistedFee: number | null; unlistedMode: string }>({ acceptUnlisted: false, unlistedFee: null, unlistedMode: "fixed" });
  const cepLookupRef = useRef("");
  // CEP que o GPS/busca do Google trouxe junto com a rua: o ViaCEP só
  // completa o que faltar nele, sem trocar a rua (ver lookupCep).
  const googleZipRef = useRef("");
  const distanceFetchRef = useRef("");
  // Ordena as respostas da cotação: só a MAIS RECENTE pode aplicar. Sem isso,
  // com duas cotações em voo (cliente editando endereço), a resposta VELHA
  // podia chegar por último e sobrescrever a nova — taxa exibida ficava stale
  // vs a autoritativa do pedido (incidente snackpetiscos 15/07/2026).
  const distanceFetchSeqRef = useRef(0);
  const [requoteTick, setRequoteTick] = useState(0);
  const manualMode: boolean = !!(store as any)?.manualAddressMode;
  // Taxa por bairro no endereço manual (12/10/2026, pedido do Pedro): o bairro é o seletor da TAXA — sem ele o pedido
  // não fecha —, então aparece sempre e é exigido, mesmo desligado na config (como no fluxo da busca). Desligado, o
  // cliente ficava sem ter onde escolher e via só «Preencha o endereço de entrega».
  const manualFields = useMemo(() => {
    const f = resolveManualAddressFields((store as any)?.manualAddressFields);
    return (store as any)?.manualAddressMode && (store as any)?.deliveryFeeType === "neighborhood"
      ? { ...f, neighborhood: { enabled: true, required: true } }
      : f;
  }, [store]);
  const rawFeeType: string = (store as any)?.deliveryFeeType || "fixed";
  const feeType: string = manualMode && rawFeeType === "distance" ? "fixed" : rawFeeType;
  // Taxa por distância no «Digitar endereço manualmente» (Pedro, 22/10/2026): sem mapa, o CEP é o que cota a taxa, então
  // aparece e é exigido mesmo desligado na config, como o bairro na taxa por bairro. Desligado, o cliente sem GPS ficava
  // em «Escolha o endereço na busca», e o servidor recusa distância sem coordenadas nem CEP. Na busca, segue a config.
  const camposDaBusca = useMemo(
    () => (feeType === "distance" && userManualEntry && !manualFields.cep.enabled
      ? { ...manualFields, cep: { enabled: true, required: true } }
      : manualFields),
    [feeType, userManualEntry, manualFields],
  );
  // Campos que o AddressAutocomplete REALMENTE renderiza — só eles podem ser
  // exigidos. No painel pós-busca ele roda compacto (sem cidade/UF/CEP); exigir
  // um campo que não está na tela travaria o pedido sem o cliente ter o que fazer.
  const hasStoredFieldConfig = !!(store as any)?.manualAddressFields;
  // A entrega POR BAIRRO usa o mesmo AddressAutocomplete da distância desde
  // 08/08/2026 (select do bairro + fluxo GPS/busca/manual), mas continua fora
  // de usesAutocomplete: a validação dela é o par nbBaseOk/nbRequiredOk, que
  // além dos obrigatórios cobre o gate "bairro sozinho não fecha pedido".
  const usesAutocomplete = !manualMode && feeType !== "neighborhood";
  // Com config salva, o painel pós-busca desenha TODOS os campos habilitados
  // (inclusive cidade/UF/CEP no compacto — o componente passou a honrar a
  // config integralmente em 08/08/2026), então todos são exigíveis.
  const autocompleteVisibleKeys: ManualAddressFieldKey[] = !hasStoredFieldConfig || !usesAutocomplete
    ? []
    : MANUAL_ADDRESS_FIELD_ORDER;
  const missingRequiredLabels = (keys: ManualAddressFieldKey[]): string[] =>
    keys
      .filter(k => camposDaBusca[k].enabled && camposDaBusca[k].required)
      .filter(k => {
        const v = (address as any)[MANUAL_ADDRESS_ADDRESS_KEY[k] as keyof typeof address];
        return v == null || String(v).trim() === "";
      })
      .map(k => MANUAL_ADDRESS_FIELD_LABELS[k]);

  const createOrderMutation = useCreateOrder();

  const resetAddressForm = useCallback(() => {
    setAddress({ street: "", number: "", complement: "", neighborhood: "", city: "", state: "", zipCode: "", reference: "", lat: null, lng: null });
    googleZipRef.current = "";
    setSelectedNeighborhood("");
    setAddressLabel("");
    setEditingAddressId(null);
    cepLookupRef.current = "";
    distanceFetchRef.current = "";
  }, []);

  const applyAddressFromSaved = useCallback((addr: SavedAddress) => {
    setAddress({
      street: addr.street,
      number: addr.number,
      complement: addr.complement || "",
      neighborhood: addr.neighborhood,
      city: addr.city,
      state: addr.state || "",
      zipCode: addr.zipCode,
      reference: addr.reference || "",
      lat: addr.lat,
      lng: addr.lng,
    });
    cepLookupRef.current = addr.zipCode.replace(/\D/g, "");
    distanceFetchRef.current = "";
    setSelectedNeighborhood(prev => {
      if (prev === addr.neighborhood) {
        if (feeType === "neighborhood") {
          const target = normalizeNeighborhood(addr.neighborhood);
          const match = neighborhoodList.find(n => normalizeNeighborhood(n.name) === target);
          if (match) {
            setDynamicFee({ fee: match.fee, distanceKm: null, noDelivery: false, neighborhood: match.name });
          } else if (neighborhoodList.length > 0) {
            // Mesmo invariante do efeito de cotação: endereço salvo cujo bairro
            // não está na lista não pré-seleciona nem cota — o cliente escolhe.
            setDynamicFee(null);
            return "";
          }
        }
        return prev;
      }
      return addr.neighborhood;
    });
  }, [feeType, neighborhoodList, neighborhoodMeta]);

  const fetchSavedAddresses = useCallback(async (): Promise<SavedAddress[]> => {
    try {
      const data = await customerCustomFetch<SavedAddress[]>(`/api/catalog/${slug}/addresses`);
      setSavedAddresses(data);
      return data;
    } catch {
      return [];
    }
  }, [slug]);

  useEffect(() => {
    if (!customer || initialAddressLoadRef.current) return;
    initialAddressLoadRef.current = true;
    fetchSavedAddresses().then(list => {
      if (list.length === 0) {
        setAddressFormMode("new");
        return;
      }
      const def = list.find(a => a.isDefault) || list[0];
      setSelectedAddressId(def.id);
      applyAddressFromSaved(def);
      setAddressFormMode("hidden");
    });
  }, [customer, fetchSavedAddresses, applyAddressFromSaved]);

  const handleSelectSavedAddress = (id: string) => {
    if (id === "__new__") {
      setSelectedAddressId("");
      resetAddressForm();
      setAddressFormMode("new");
      return;
    }
    const addr = savedAddresses.find(a => a.id === id);
    if (!addr) return;
    setSelectedAddressId(id);
    applyAddressFromSaved(addr);
    setAddressFormMode("hidden");
  };

  const handleEditSaved = (id: string) => {
    const addr = savedAddresses.find(a => a.id === id);
    if (!addr) return;
    setSelectedAddressId(id);
    applyAddressFromSaved(addr);
    setAddressLabel(addr.label || "");
    setEditingAddressId(id);
    setAddressFormMode("edit");
  };

  const handleDeleteSaved = async (id: string) => {
    if (!customer) return;
    if (!(await confirm({ title: "Remover este endereço?", confirmLabel: "Remover" }))) return;
    try {
      try {
        await customerCustomFetch(`/api/catalog/${slug}/addresses/${id}`, { method: "DELETE" });
      } catch (e: any) {
        toast({ title: extractApiErrorMessage(e, "Não foi possível remover"), variant: "destructive" });
        return;
      }
      const remaining = savedAddresses.filter(a => a.id !== id);
      setSavedAddresses(remaining);
      if (selectedAddressId === id) {
        if (remaining.length > 0) {
          const next = remaining.find(a => a.isDefault) || remaining[0];
          setSelectedAddressId(next.id);
          applyAddressFromSaved(next);
          setAddressFormMode("hidden");
        } else {
          setSelectedAddressId("");
          resetAddressForm();
          setAddressFormMode("new");
        }
      }
    } catch {
      toast({ title: errorTitle("remover o endereço"), description: TRY_AGAIN_DESCRIPTION, variant: "destructive" });
    }
  };

  // labelOverride: usado pelo auto-save do 1º endereço ("Casa") disparado no
  // "Confirmar localização" — sem passar pelo input de rótulo.
  const handleSaveAddressForm = async (labelOverride?: string) => {
    if (!customer) return;
    if (manualMode) {
      const missing: string[] = [];
      for (const k of MANUAL_ADDRESS_FIELD_ORDER) {
        const cfg = manualFields[k];
        if (cfg.enabled && cfg.required && !fieldFilled(k)) missing.push(MANUAL_ADDRESS_FIELD_LABELS[k]);
      }
      if (missing.length > 0) {
        toast({ title: `Preencha: ${missing.join(", ")}`, variant: "destructive" });
        return;
      }
    } else {
      // Rua/nº/bairro: só os DESENHADOS (campo desligado na config não pode
      // travar o salvamento — não há onde preencher).
      const missingBase = (["street", "number", "neighborhood"] as ManualAddressFieldKey[])
        .filter(k => drawnBase(k) && !fieldFilled(k))
        .map(k => MANUAL_ADDRESS_FIELD_LABELS[k]);
      if (missingBase.length > 0) {
        toast({ title: `Preencha: ${missingBase.join(", ")}`, variant: "destructive" });
        return;
      }
      // Obrigatórios da config valem também ao SALVAR: endereço salvo pela o
      // gate do formulário fechado, então salvar incompleto viraria furo.
      const missing = missingRequiredLabels(usesAutocomplete ? autocompleteVisibleKeys : nbDrawnKeys);
      if (missing.length > 0) {
        toast({ title: `Preencha: ${missing.join(", ")}`, variant: "destructive" });
        return;
      }
    }
    setSavingAddress(true);
    try {
      const payload = {
        street: address.street,
        number: address.number,
        complement: address.complement || null,
        neighborhood: address.neighborhood,
        city: address.city,
        state: address.state || null,
        zipCode: address.zipCode,
        reference: address.reference || null,
        lat: manualMode ? null : address.lat,
        lng: manualMode ? null : address.lng,
        label: (labelOverride ?? addressLabel) || null,
        isDefault: false,
      };
      const isEdit = addressFormMode === "edit" && editingAddressId;
      const url = isEdit
        ? `/api/catalog/${slug}/addresses/${editingAddressId}`
        : `/api/catalog/${slug}/addresses`;
      let saved: SavedAddress;
      try {
        saved = await customerCustomFetch<SavedAddress>(url, {
          method: isEdit ? "PUT" : "POST",
          body: JSON.stringify(payload),
        });
      } catch (e: any) {
        toast({ title: extractApiErrorMessage(e, "Não foi possível salvar"), variant: "destructive" });
        return;
      }
      const list = await fetchSavedAddresses();
      const fresh = list.find(a => a.id === saved.id) || saved;
      setSelectedAddressId(fresh.id);
      applyAddressFromSaved(fresh);
      setAddressLabel("");
      setEditingAddressId(null);
      setAddressFormMode("hidden");
      toast({ title: isEdit ? "Endereço atualizado" : "Endereço salvo" });
    } catch {
      toast({ title: errorTitle("salvar o endereço"), description: TRY_AGAIN_DESCRIPTION, variant: "destructive" });
    } finally {
      setSavingAddress(false);
    }
  };

  const handleCancelAddressForm = () => {
    if (savedAddresses.length === 0) return;
    setAddressLabel("");
    setEditingAddressId(null);
    if (selectedAddressId) {
      const addr = savedAddresses.find(a => a.id === selectedAddressId);
      if (addr) applyAddressFromSaved(addr);
    } else {
      const def = savedAddresses.find(a => a.isDefault) || savedAddresses[0];
      setSelectedAddressId(def.id);
      applyAddressFromSaved(def);
    }
    setAddressFormMode("hidden");
  };

  useEffect(() => {
    if (!customerLoading && !customer) {
      setLoginOpen(true);
    }
  }, [customerLoading, customer]);

  useStoreColors(store);

  useEffect(() => {
    if (manualMode) return;
    fetch(`${getApiBase()}/api/catalog/config/maps-key`).then(r => r.json()).then(d => { if (d.key) setGoogleMapsKey(d.key); }).catch(() => {});
  }, [manualMode]);

  useEffect(() => {
    if (manualMode && feeType === "neighborhood") {
      const next = (address.neighborhood || "").trim();
      setSelectedNeighborhood(prev => {
        // No estado "__other__" o cliente digita o bairro em texto livre e
        // address.neighborhood carrega o que foi digitado (ou "" recém-limpo
        // pelo onChange do select) — sincronizar de volta aqui desfazia a
        // seleção de "Outro bairro" no mesmo render.
        const target = normalizeNeighborhood(next);
        const match = next ? neighborhoodList.find(n => normalizeNeighborhood(n.name) === target) : undefined;
        if (match) return prev === match.name ? prev : match.name;
        if (prev === "__other__") return prev;
        if (!next) return prev === "" ? prev : "";
        // Bairro fora da lista (ex.: endereço salvo antigo): cai no "Outro
        // bairro" quando a loja aceita, senão mantém o cru.
        return neighborhoodMeta.acceptUnlisted ? "__other__" : next;
      });
    }
  }, [manualMode, feeType, address.neighborhood, neighborhoodList, neighborhoodMeta.acceptUnlisted]);

  // Por bairro sem nenhum bairro com nome e sem aceitar não cadastrado: a entrega
  // não tem saída (o seletor da taxa fica vazio). O checkout abre em Retirada e
  // diz por quê; loja só de entrega mostra o aviso no lugar do endereço.
  const entregaSemSaida = !!store && entregaPorBairroSemSaida(store as any);
  // Loja sem entrega e sem retirada: o pedido sai «A combinar» e o card «Como deseja receber?» some (Pedro, 19/10/2026:
  // «Se a loja não oferece nem entrega nem retirada, não tem que fixar retirada»).
  const semModalidade = !!store && !store.allowsDelivery && !store.allowsPickup;
  useEffect(() => {
    if (store) {
      const modo = semModalidade ? "combinar" : modoDeEntregaInicial(store as any);
      if (modo) setDeliveryMode(modo);
    }
  }, [store]);

  const [cartHydrated, setCartHydrated] = useState(() => useCart.persist?.hasHydrated?.() ?? true);
  useEffect(() => {
    setCartHydrated(useCart.persist?.hasHydrated?.() ?? true);
    const unsub = useCart.persist?.onFinishHydration?.(() => setCartHydrated(true));
    return () => unsub?.();
  }, [useCart]);

  useEffect(() => {
    if (!cartHydrated) return;
    if (!loadingCatalog && items.length === 0) setLocation(`/loja/${slug}`);
  }, [items, loadingCatalog, cartHydrated]);

  // "Voltar" do checkout: em navegadores in-app (ex: WhatsApp) o storefront
  // costuma ser a 1ª entrada do WebView, e o voltar nativo escapa da SPA caindo
  // num 404. Empurramos uma sentinela no histórico e tratamos o popstate pra
  // SEMPRE voltar pra loja com o carrinho aberto — previsível em qualquer
  // navegador (e atende a expectativa de "voltar pro carrinho").
  useEffect(() => {
    if (!slug) return;
    const backTarget = `/loja/${slug}?${unitId ? `unitId=${encodeURIComponent(unitId)}&` : ""}cart=open`;
    window.history.pushState(null, "", window.location.href);
    const onPopState = () => setLocation(backTarget, { replace: true });
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, unitId]);

  useEffect(() => {
    if (feeType !== "neighborhood" || !store) return;
    // Lê direto do objeto store (já vem do /api/catalog/:slug). Antes
    // dependia de um fetch separado para /delivery-fee, que falhava
    // silenciosamente em alguns ambientes deixando o dropdown vazio.
    const raw = (store as any)?.deliveryNeighborhoods;
    const list: Array<{ name: string; fee: number; minOrder: number }> = Array.isArray(raw)
      ? raw
          .map((n: any) => ({
            name: String(n?.name ?? "").trim(),
            fee: typeof n?.fee === "number" ? n.fee : parseFloat(n?.fee ?? "0") || 0,
            minOrder: typeof n?.minOrder === "number" ? n.minOrder : parseFloat(n?.minOrder ?? "0") || 0,
          }))
          .filter((n) => n.name.length > 0)
      : [];
    setNeighborhoodList((prev) => {
      if (
        prev.length === list.length &&
        prev.every((p, i) => p.name === list[i].name && p.fee === list[i].fee && p.minOrder === list[i].minOrder)
      ) {
        return prev;
      }
      return list;
    });
    const acceptUnlisted = !!(store as any)?.acceptUnlistedNeighborhoods;
    const rawUnlisted = (store as any)?.unlistedNeighborhoodFee;
    const unlistedFee =
      rawUnlisted == null || rawUnlisted === ""
        ? null
        : typeof rawUnlisted === "number"
          ? rawUnlisted
          : parseFloat(String(rawUnlisted)) || 0;
    const unlistedMode = String((store as any)?.unlistedNeighborhoodMode ?? "fixed");
    setNeighborhoodMeta((prev) =>
      prev.acceptUnlisted === acceptUnlisted && prev.unlistedFee === unlistedFee && prev.unlistedMode === unlistedMode
        ? prev
        : { acceptUnlisted, unlistedFee, unlistedMode },
    );
  }, [feeType, store]);

  const lookupCep = useCallback(async (cep: string) => {
    const clean = cep.replace(/\D/g, "");
    if (clean.length !== 8) return;
    if (cepLookupRef.current === clean) return;
    cepLookupRef.current = clean;

    try {
      const qs = unitId ? `&unitId=${unitId}` : "";
      const resp = await fetch(`${getApiBase()}/api/catalog/${slug}/viacep?zipCode=${clean}${qs}`, { credentials: "include" });
      if (resp.ok) {
        const data = await resp.json();
        // Com frete por bairro a lista do lojista é a AUTORIDADE: o CEP
        // preenche rua e cidade e não encosta no bairro. O nome oficial do
        // ViaCEP ("Jardim Grajaú") nunca bate com o que o lojista digitou
        // ("Jd Grajau"), então sobrescrever a escolha do cliente desfazia
        // ela em silêncio e jogava o pedido na taxa de bairro não cadastrado
        // — R$ 0,00 na maioria das lojas (Estação do Sabor, 08/2026).
        const listIsAuthority = feeType === "neighborhood";
        // CEP que veio do Google com a rua: só completa campo VAZIO. CEP de
        // conjunto cobre uma faixa de quadras (64030-160 = «Quadra Bela
        // Vista», de 64/65 a 66/67) e trocar a rua apagava a quadra que o
        // Google acertou («Conj. Bela Vista II Qu 66») — Puro Cacau, 10/10.
        // CEP digitado pelo cliente segue preenchendo como sempre.
        const fromGoogle = googleZipRef.current === clean;
        setAddress(prev => ({
          ...prev,
          street: fromGoogle ? (prev.street || data.street || "") : (data.street || prev.street),
          neighborhood: listIsAuthority
            ? prev.neighborhood
            : fromGoogle
              ? (prev.neighborhood || data.neighborhood || "")
              : (data.neighborhood || prev.neighborhood),
          city: fromGoogle ? (prev.city || data.city || "") : (data.city || prev.city),
        }));
      }
    } catch {}

  }, [slug, unitId, feeType]);

  useEffect(() => {
    const clean = address.zipCode.replace(/\D/g, "");
    if (clean.length === 8) {
      lookupCep(clean);
    }
  }, [address.zipCode, lookupCep]);

  useEffect(() => {
    if (feeType !== "distance" || deliveryMode !== "entrega") return;
    const hasCoords = address.lat != null && address.lng != null;
    const clean = address.zipCode.replace(/\D/g, "");
    if (!hasCoords && clean.length !== 8) return;
    const key = hasCoords ? `${address.lat}|${address.lng}` : `${clean}|${address.street}|${address.number}|${address.city}|${address.neighborhood}`;
    if (distanceFetchRef.current === key) return;
    distanceFetchRef.current = key;
    const seq = ++distanceFetchSeqRef.current;
    setFeeLoading(true);
    const params = new URLSearchParams();
    if (hasCoords) {
      params.set("lat", String(address.lat));
      params.set("lng", String(address.lng));
    }
    if (clean) params.set("zipCode", clean);
    if (address.street) params.set("street", address.street);
    if (address.number) params.set("number", address.number);
    if (address.city) params.set("city", address.city);
    if (address.neighborhood) params.set("neighborhood", address.neighborhood);
    if (unitId) params.set("unitId", unitId);
    fetch(`${getApiBase()}/api/catalog/${slug}/delivery-fee?${params.toString()}`, { credentials: "include" })
      .then(r => {
        if (!r.ok) { if (seq === distanceFetchSeqRef.current) setDynamicFee(null); return null; }
        return r.json();
      })
      .then(data => { if (data && seq === distanceFetchSeqRef.current) setDynamicFee(data); })
      .catch(() => { if (seq === distanceFetchSeqRef.current) setDynamicFee(null); })
      .finally(() => { if (seq === distanceFetchSeqRef.current) setFeeLoading(false); });
  }, [feeType, deliveryMode, address.zipCode, address.street, address.number, address.city, address.neighborhood, address.lat, address.lng, slug, unitId, requoteTick]);

  useEffect(() => {
    if (feeType === "neighborhood" && selectedNeighborhood) {
      if (selectedNeighborhood === "__other__") {
        // Não limpar address.neighborhood aqui: nesse estado ele carrega o
        // texto digitado pelo cliente (o onChange do select já limpa quando
        // "Outro bairro" é escolhido ativamente).
        setDynamicFee(
          neighborhoodMeta.unlistedMode === "on_request"
            ? { fee: null, distanceKm: null, noDelivery: false, neighborhood: "", feePending: true }
            : { fee: neighborhoodMeta.unlistedFee ?? 0, distanceKm: null, noDelivery: false, neighborhood: "" },
        );
        return;
      }
      // Aguarda a lista de bairros carregar antes de marcar noDelivery — evita
      // falso "Não entregamos nesse bairro" enquanto o fetch está em voo.
      if (neighborhoodList.length === 0) {
        setAddress(prev => (prev.neighborhood === selectedNeighborhood ? prev : { ...prev, neighborhood: selectedNeighborhood }));
        return;
      }
      const target = normalizeNeighborhood(selectedNeighborhood);
      const match = neighborhoodList.find(n => normalizeNeighborhood(n.name) === target);
      if (match) {
        setDynamicFee({ fee: match.fee, distanceKm: null, noDelivery: false, neighborhood: match.name });
        setAddress(prev => ({ ...prev, neighborhood: selectedNeighborhood }));
        return;
      }
      // INVARIANTE: com frete por bairro, selectedNeighborhood só pode ser um
      // nome da lista, "__other__" escolhido de propósito, ou vazio. Bairro
      // fora da lista que o cliente não escolheu (endereço salvo antigo,
      // resíduo do autofill de CEP) zera a seleção e ele escolhe — o card já
      // diz "Escolha o bairro para calcularmos a entrega". Antes isso caía
      // direto na taxa de bairro não cadastrado, R$ 0,00 em 99 lojas.
      setSelectedNeighborhood("");
      setDynamicFee(null);
    }
  }, [selectedNeighborhood, neighborhoodList, neighborhoodMeta, feeType]);

  // Parte de UM cupom nos produtos (só pra "você economiza" quando há mais de
  // um cupom): percentual sobre o subtotal; fixo limitado ao subtotal; frete = 0.
  const descontoDoCupom = (c: { discountType: "percent" | "fixed" | "free_delivery"; discountValue: number }) =>
    c.discountType === "free_delivery" ? 0
      : c.discountType === "fixed" ? Math.min(total, c.discountValue)
        : Math.round(total * (c.discountValue / 100) * 100) / 100;

  // Frete a fechar depois pelo lojista: a loja inteira é "on_request", ou o
  // bairro caiu fora da lista numa loja que escolheu calcular depois. Nos dois
  // casos o total é PARCIAL — não dá pra mostrar 0 como "grátis".
  const feePending =
    deliveryMode === "entrega" &&
    (feeType === "on_request" || dynamicFee?.feePending === true);

  // Taxa cotada ANTES do frete grátis — a conta abaixo decide o resto.
  let rawDeliveryFee = 0;
  if (deliveryMode === "entrega") {
    if (feeType === "fixed") {
      rawDeliveryFee = parseFloat((store as any)?.deliveryFee ?? "0") || 0;
    } else if (feeType === "on_request") {
      rawDeliveryFee = 0;
    } else if (dynamicFee && !dynamicFee.noDelivery && dynamicFee.fee !== null) {
      rawDeliveryFee = dynamicFee.fee;
    }
  }

  // ESPELHO da conta do servidor (mesma função, @workspace/pricing): parte do
  // cupom nos produtos → base do frete grátis (cheio ou pós-cupom, escolha da
  // loja) → taxa real → parte do cupom na entrega → entrega líquida. Ver
  // couponMath.ts. Se isto e o servidor divergirem, o cliente vê um total e
  // paga outro.
  const cupomMath = computeCouponMath({
    subtotal: total,
    coupons: appliedCoupons,
    deliveryMode,
    rawDeliveryFee,
    feePending,
    freeDeliveryAbove: (store as any)?.freeDeliveryAbove,
    freeDeliveryBeforeCoupon: (store as any)?.freeDeliveryBeforeCoupon === true,
    couponCoversDelivery: (store as any)?.couponCoversDelivery === true,
  });
  const couponDiscount = cupomMath.couponDiscount;
  // Prêmios de pontos: DEPOIS do cupom, antes do acréscimo — espelho do servidor (computeRewardMath).
  const programa = loyalty?.enabled ? loyalty.program : undefined;
  const saldoPontos = loyalty?.verified ? loyalty.balance ?? 0 : 0;
  const premioCupomBloqueado = !!programa && appliedCoupons.length > 0 && !programa.allowWithCoupon;
  const problemaDoPremio = (r: NonNullable<typeof programa>["rewards"][number], saldoLivre = saldoPontos) =>
    premioCupomBloqueado
      ? "Não vale junto com cupom."
      : rewardProblem({
          reward: { ...r, stock: null, stockSetAt: null, active: true },
          balance: saldoLivre,
          deliveryMode,
          feePending,
          netDelivery: cupomMath.netDelivery,
          stockLeft: r.stockLeft,
        });
  const rewardUses: RewardUse[] = [];
  if (programa && loyalty?.verified) {
    let livre = saldoPontos;
    for (const e of premiosEscolhidos) {
      const r = programa.rewards.find((x) => x.id === e.rewardId);
      if (!r || problemaDoPremio(r, livre)) continue;
      const points = r.type === "cashback" ? Math.max(r.cost, Math.min(livre, e.points ?? livre)) : r.cost;
      livre -= points;
      rewardUses.push({ rewardId: r.id, name: r.name, type: r.type, value: r.value, points });
    }
  }
  const rewardMath = computeRewardMath({ netProducts: cupomMath.netProducts, netDelivery: cupomMath.netDelivery, uses: rewardUses });
  rewardUses.forEach((u, i) => { u.points = rewardMath.pointsPerUse[i] ?? u.points; });
  const premiosNoPedido = rewardUses.filter((u) => u.points > 0);
  const totalAfterCoupon = rewardMath.netProducts;
  // Taxa REAL (o servidor grava esta; o cupom a cobre pelo desconto).
  const deliveryFee = cupomMath.deliveryFee;
  const deliveryFreeApplied = cupomMath.freeApplied;
  const netDelivery = rewardMath.netDelivery;
  const premioFreteZeraTaxa = rewardMath.deliveryDiscount > 0 && deliveryFee > 0;
  const premiosComDesconto = premiosNoPedido
    .map((u) => ({ u, d: rewardMath.perUse[rewardUses.indexOf(u)] ?? 0 }))
    .filter((x) => x.d > 0 && x.u.type !== "free_delivery");
  // Quanto este pedido rende (no Entregue): produtos líquidos, dia da semana da loja.
  const rendimentoPontos = programa
    ? pointsForOrder(normalizeLoyaltyConfig(programa), { base: rewardMath.netProducts, weekday: diaDaSemanaNaLoja(getStoreTimezone()) })
    : 0;
  // Cupom de frete cobrindo uma taxa de verdade: a linha da entrega sai
  // riscada ("~R$ 7,00~ Grátis") e o cupom NÃO ganha linha própria — senão o
  // leitor somava o desconto duas vezes.
  const cupomFreteZeraTaxa = cupomMath.hasFreeDeliveryCoupon && deliveryFee > 0;
  const cupomDaLinha = appliedCoupons.find((c) => c.discountType !== "free_delivery") ?? null;
  // Linha "Cupom" do resumo: tudo que o cupom desconta MENOS o que o cupom de
  // frete cobre (já dito na linha da entrega). Com "abate entrega" ligado a
  // conta fica honesta: Subtotal 25 · Cupom -30 · Entrega 7 · Total 2.
  const couponLineAmount = Math.round((cupomMath.productDiscount + (cupomMath.hasFreeDeliveryCoupon ? 0 : cupomMath.deliveryDiscount)) * 100) / 100;
  const cuponsSemFrete = appliedCoupons.filter((c) => c.discountType !== "free_delivery");
  const economiaDoCupom = (c: { code: string; discountType: "percent" | "fixed" | "free_delivery"; discountValue: number }) =>
    c.discountType === "free_delivery"
      ? deliveryFee
      : cuponsSemFrete.length === 1 ? couponLineAmount : Math.min(total, descontoDoCupom(c));

  const noDeliveryBlocked = deliveryMode === "entrega" && feeType !== "fixed" && feeType !== "on_request" && dynamicFee?.noDelivery === true;
  const feeNotResolved = deliveryMode === "entrega" && feeType !== "fixed" && feeType !== "on_request" && (dynamicFee === null || feeLoading);
  // SÓ TEXTO. "Sem taxa resolvida" tem duas causas bem diferentes: está
  // calculando (esperar resolve) ou falta uma escolha do cliente (esperar não
  // resolve nunca). Mandar "aguarde o cálculo" pra quem só não escolheu o
  // bairro deixa o cliente parado esperando algo que não vem.
  const feeNotResolvedMsg = feeLoading
    ? "Aguarde o cálculo da taxa de entrega."
    : feeType === "neighborhood"
      ? (selectedNeighborhood === "__other__" && !address.neighborhood.trim()
          ? "Digite o bairro para calcularmos a entrega."
          : "Escolha o bairro para calcularmos a entrega.")
      : "Confirme o endereço para calcularmos a taxa de entrega.";

  // Formas de pagamento disponíveis pra modalidade escolhida. Se a loja
  // configurou formas diferentes por modalidade, usa a lista do modo atual;
  // senão (ou antes de escolher modalidade), cai na lista única.
  const paymentsByMode = (store as any)?.acceptedPaymentsByMode as { delivery?: string[]; pickup?: string[] } | null | undefined;
  // Crediário com allowPublic entra na lista do link (qualquer modalidade).
  // O cliente ESCOLHE as parcelas até o máximo da config; juros conforme o
  // modo do método: "total" (flat) ou "parcela" (1x sem juros, +j% por
  // parcela adicional — estilo Mercado Livre).
  type CrediarioCfg = { name: string; allowPublic?: boolean; installments?: number; interestMode?: string; interestPercent?: number; interestPerInstallment?: number };
  const crediarioCfgs = (((store as any)?.crediarioMethods ?? []) as CrediarioCfg[])
    .filter((m) => m?.allowPublic && m?.name);
  const crediarioPublicNames: string[] = crediarioCfgs.map((m) => m.name);
  // «A combinar» usa a lista única da loja, a mesma que o servidor confere para esse pedido.
  const effectivePayments: string[] = ((paymentsByMode && (deliveryMode === "entrega" || deliveryMode === "retirada"))
    ? ((deliveryMode === "entrega" ? paymentsByMode.delivery : paymentsByMode.pickup) ?? [])
    : ((store as any)?.acceptedPayments ?? [])
  ).concat(crediarioPublicNames);
  const selectedCrediarioCfg = crediarioCfgs.find((m) => m.name === paymentMethod) ?? null;
  const isCrediarioMethod = !!selectedCrediarioCfg;
  const [crediarioInstallments, setCrediarioInstallments] = useState(1);
  const crediarioInterestFor = (cfg: CrediarioCfg | null, n: number) =>
    !cfg ? 0 : cfg.interestMode === "parcela"
      ? (Number(cfg.interestPerInstallment) || 0) * Math.max(n - 1, 0)
      : (Number(cfg.interestPercent) || 0);
  // Trocou de método → volta pra 1x (o máximo pode ser menor no método novo).
  useEffect(() => { setCrediarioInstallments(1); }, [paymentMethod]);

  // Se trocar de modalidade e o método selecionado não valer mais, limpa a seleção.
  useEffect(() => {
    if (paymentMethod && !effectivePayments.includes(paymentMethod)) {
      setPaymentMethod("");
      setNeedsChange(null);
      setChangeFor("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryMode, paymentMethod, store]);

  const singleSurcharge = computeCardSurcharge(
    (store as any)?.cardSurchargeConfig,
    paymentMethod,
    totalAfterCoupon,
    netDelivery,
  );

  // Misto: parte 2 = restante. Acréscimo por PARTE (mesma conta do PDV, frete
  // rateado proporcionalmente); como o acréscimo depende das partes, itera até
  // convergir (percentual < 100% converge em poucas passadas).
  const round2 = (n: number) => Math.round(n * 100) / 100;
  // A entrega entra como parâmetro: a TELA usa 0 enquanto o valor da entrega
  // por bairro está escondido (taxaOculta); o envio sempre usa a real.
  const calcularMisto = (entrega: number) => {
    if (!paymentMixed) return null;
    const m1 = mixedMethod1 || effectivePayments[0] || "";
    const m2 = mixedMethod2 || effectivePayments.find((p) => p !== m1) || "";
    const a1 = round2(Math.max(0, mixedAmount1));
    const cfgMap = (store as any)?.cardSurchargeConfig ?? null;
    const base = totalAfterCoupon + entrega;
    let a2 = round2(Math.max(0, base - a1));
    let surcharge = 0;
    for (let i = 0; i < 4; i++) {
      const partsSum = a1 + a2 || 1;
      surcharge = round2(
        computeCardSurcharge(cfgMap, m1, a1, (a1 / partsSum) * entrega) +
        computeCardSurcharge(cfgMap, m2, a2, (a2 / partsSum) * entrega),
      );
      a2 = round2(Math.max(0, base + surcharge - a1));
    }
    return { m1, m2, a1, a2, surcharge };
  };
  const mixed = calcularMisto(netDelivery);

  const cardSurcharge = paymentMixed && mixed ? mixed.surcharge : singleSurcharge;
  const mixedCashAmount = mixed ? (mixed.m1 === "Dinheiro" ? mixed.a1 : mixed.m2 === "Dinheiro" ? mixed.a2 : 0) : 0;

  const finalTotal = computeOrderTotal({
    subtotal: total,
    discount: couponDiscount + rewardMath.loyaltyDiscount,
    deliveryFee,
    surcharge: cardSurcharge,
  });

  const cepValid = address.zipCode.replace(/\D/g, "").length === 8;
  const hidesCepCity = feeType === "neighborhood" && neighborhoodList.length > 0 && !neighborhoodMeta.acceptUnlisted;
  // Bairros em ordem alfabética e SEM o preço na opção (decisão do Pedro,
  // 02/10/2026): com "Nome — R$ x" o cliente escolhia o mais barato de nome
  // parecido — Potinhos de amoor: dos 17 pedidos em "Cumbica" com CEP, 16 eram
  // de outro bairro. O valor só aparece com bairro e endereço completos
  // (ver taxaOculta). O value continua o nome cadastrado: a taxa sai dele.
  const bairrosEmOrdem = [...neighborhoodList].sort((a, b) =>
    a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base", numeric: true }),
  );
  const distanceFeeResolved = feeType === "distance" && dynamicFee && !dynamicFee.noDelivery;
  // No endereço manual com taxa por bairro, o bairro conta como preenchido só quando ESCOLHIDO no seletor (um da lista,
  // ou "Outro bairro" com o nome digitado): o texto de um endereço salvo que não está na lista não dá taxa.
  const bairroDaTaxaNoManual = manualMode && feeType === "neighborhood";
  // Também é o gate do bairro na busca/GPS: «Outro bairro» em branco passava no
  // navegador e o servidor recusava ("Bairro obrigatório").
  const bairroEscolhido = bairroDaTaxaEscolhido(selectedNeighborhood, address.neighborhood, neighborhoodList.map(n => n.name));
  const fieldFilled = (k: ManualAddressFieldKey) => {
    if (k === "neighborhood" && bairroDaTaxaNoManual) return bairroEscolhido;
    const v = (address as any)[MANUAL_ADDRESS_ADDRESS_KEY[k] as keyof typeof address];
    return v != null && String(v).trim() !== "";
  };
  // Campos que o formulário POR BAIRRO desenha quando a loja salvou a config
  // de campos: rua/nº/bairro sempre (são o endereço do motoboy) + os que ela
  // habilitou. Obrigatório SÓ vale para campo desenhado — exigir campo fora da
  // tela foi o incidente de 25/07 (153 lojas). Sem config salva, nada é
  // exigido aqui: o default do código marca number/street como required e
  // aplicá-lo barraria pedidos de 373 lojas cujo lojista nunca escolheu nada.
  const nbDrawnKeys: ManualAddressFieldKey[] = hasStoredFieldConfig
    ? MANUAL_ADDRESS_FIELD_ORDER.filter(k =>
        k === "street" || k === "number" || k === "neighborhood"
          ? true
          : manualFields[k].enabled,
      )
    : [];
  // Só exige com o formulário ABERTO: quem escolheu endereço salvo está com
  // ele escondido e não teria onde preencher (invariante de 25/07).
  const nbFormClosed =
    manualMode || feeType !== "neighborhood" ||
    (savedAddresses.length > 0 && addressFormMode === "hidden");
  const nbRequiredOk =
    nbFormClosed || !hasStoredFieldConfig
      ? true
      : nbDrawnKeys.every(
          k => !(manualFields[k].enabled && manualFields[k].required) || fieldFilled(k),
        );
  // Gate do Pedro (08/08): bairro sozinho NÃO fecha pedido. Com o fluxo
  // GPS/busca recolhido, rua e nº ficam fora da tela até o cliente interagir —
  // sem este gate, pedido sem endereço volta (17 pedidos em 08/08). Vale para
  // TODA loja por bairro, com ou sem config salva; endereço salvo (formulário
  // fechado) passa, porque já foi aceito antes.
  const nbBaseOk =
    nbFormClosed ||
    (["street", "number"] as ManualAddressFieldKey[]).every(
      k => !(!hasStoredFieldConfig || manualFields[k].enabled) || fieldFilled(k),
    );
  const manualRequiredOk = manualMode
    ? MANUAL_ADDRESS_FIELD_ORDER.every(k => {
        const cfg = manualFields[k];
        if (!cfg.enabled || !cfg.required) return true;
        return fieldFilled(k);
      })
    : true;
  // Os obrigatórios da loja também valem na busca do Google (é o que a tela de
  // configuração promete) — limitado aos campos que aparecem lá E só com o
  // formulário aberto: cliente que escolheu endereço salvo não tem onde
  // preencher, e exigir aí seria botão travado sem saída (o campo aparece pra
  // ele quando editar o endereço ou cadastrar outro).
  const autocompleteRequiredOk = manualMode || addressFormMode === "hidden"
    ? true
    : autocompleteVisibleKeys.every(k => {
        const cfg = camposDaBusca[k];
        if (!cfg.enabled || !cfg.required) return true;
        return fieldFilled(k);
      });
  // Campo desenhado na tela atual. Rua/nº/bairro deixaram de ser exceção de
  // código em 08/08/2026: quem tinha o trio desligado da época do toggle
  // inerte foi normalizado pra enabled no banco, então desligado é escolha.
  const drawnBase = (k: ManualAddressFieldKey) => !hasStoredFieldConfig || manualFields[k].enabled;
  // Taxa FIXA, cliente no «Digitar endereço manualmente» e sem ponto (GPS/busca): a alternativa ao ponto é cidade + CEP,
  // mas só os DESENHADOS. A loja que desligou CEP ou Cidade deixava o cliente sem saída: o aviso mandava usar a busca,
  // que é o que tinha falhado (Aprender #241, 21/10/2026: 656 lojas fixas assim). Campo desenhado segue cobrado como
  // antes; distância e bairro não mudam.
  const cidadeCepOk = feeType === "fixed"
    ? (!drawnBase("city") || !!address.city) && (!drawnBase("cep") || cepValid)
    : !!address.city && cepValid;
  // Só um dos dois na tela: o aviso nomeia esse, porque o outro não é cobrado.
  const soUmDeCidadeCep = feeType === "fixed" && userManualEntry && drawnBase("city") !== drawnBase("cep");
  // KEYS primeiro (o submit foca o 1º campo faltante); labels derivam delas.
  // Nomeia o que falta em vez do genérico "Preencha o endereço" — Coxinha do
  // vale (25/07): "preencha o endereço" não diz nada pra quem já preencheu
  // tudo que vê.
  const missingAddressKeys: ManualAddressFieldKey[] = (() => {
    if (deliveryMode !== "entrega") return [];
    const out: ManualAddressFieldKey[] = [];
    const push = (k: ManualAddressFieldKey) => {
      if (!out.includes(k)) out.push(k);
    };
    if (manualMode) {
      for (const k of MANUAL_ADDRESS_FIELD_ORDER) {
        if (manualFields[k].enabled && manualFields[k].required && !fieldFilled(k)) push(k);
      }
    } else if (usesAutocomplete) {
      for (const k of ["street", "number", "neighborhood"] as ManualAddressFieldKey[]) {
        if (drawnBase(k) && !fieldFilled(k)) push(k);
      }
      for (const k of autocompleteVisibleKeys) {
        if (camposDaBusca[k].enabled && camposDaBusca[k].required && !fieldFilled(k)) push(k);
      }
    } else if (feeType === "neighborhood") {
      if (!bairroEscolhido) push("neighborhood");
      // Base do gate "bairro sozinho não fecha pedido": rua e nº.
      for (const k of ["street", "number"] as ManualAddressFieldKey[]) {
        if (drawnBase(k) && !fieldFilled(k)) push(k);
      }
      // Obrigatórios da config no formulário por bairro — só os DESENHADOS.
      for (const k of nbDrawnKeys) {
        if (manualFields[k].enabled && manualFields[k].required && !fieldFilled(k)) push(k);
      }
    }
    return out;
  })();
  const missingAddressLabels: string[] = missingAddressKeys.map(k => MANUAL_ADDRESS_FIELD_LABELS[k]);
  // Rua/nº/bairro preenchidos e ainda inválido = falta LOCALIZAÇÃO (sem
  // lat/lng e sem cidade+CEP). Dizer "preencha o endereço" pra quem preencheu
  // tudo que vê é o beco sem saída de novo — aqui a saída é a busca (ou
  // cidade+CEP, quando esses campos estão na tela).
  const localizacaoPendente = usesAutocomplete && !hidesCepCity && !(address.lat != null && address.lng != null);
  const cepCidadeNaTela = userManualEntry && (!hasStoredFieldConfig || (camposDaBusca.cep.enabled && camposDaBusca.city.enabled));
  // Por bairro com o fluxo recolhido: rua/nº ainda nem estão na tela — mandar
  // "Falta preencher: Rua" sem campo visível é beco sem saída. O aviso guia
  // pra onde os campos aparecem (GPS ou busca).
  const nbFieldsHidden =
    feeType === "neighborhood" && !manualMode && !userManualEntry &&
    !(address.lat != null && address.lng != null);
  const addressMissingMsg = missingAddressLabels.length > 0
    ? (nbFieldsHidden && (missingAddressKeys.includes("street") || missingAddressKeys.includes("number"))
        ? "Complete o endereço de entrega: use sua localização atual ou busque o endereço."
        // Endereço salvo sem bairro (formulário fechado): o seletor está no lápis.
        : bairroDaTaxaNoManual && addressFormMode === "hidden" && missingAddressKeys.includes("neighborhood")
          ? "Escolha o bairro do endereço: toque no lápis para editar."
          : `Falta preencher: ${missingAddressLabels.join(", ")}`)
    : localizacaoPendente
      ? (soUmDeCidadeCep
          ? `Preencha ${drawnBase("cep") ? "o CEP" : "a cidade"}, ou escolha o endereço na busca.`
          : cepCidadeNaTela
            ? "Preencha cidade e CEP, ou escolha o endereço na busca."
            : "Escolha o endereço na busca para confirmarmos a localização.")
      : "Preencha o endereço de entrega para continuar.";
  const neighborhoodFeeOk = feeType === "neighborhood" && dynamicFee && !dynamicFee.noDelivery;
  // Base do endereço entregável: rua/nº/bairro preenchidos QUANDO desenhados.
  // Campo que a loja desligou (escolha ativa, pós-normalização de 08/08) não
  // pode ser cobrado — seria bloqueio sem campo na tela.
  const baseAddrOk = (["street", "number", "neighborhood"] as ManualAddressFieldKey[])
    .every(k => !drawnBase(k) || fieldFilled(k));
  // Classe do campo no formulário por bairro/manual: âmbar enquanto apontado
  // como faltante e vazio (limpa sozinho ao preencher).
  const missCls = (k: ManualAddressFieldKey) =>
    flagMissing.includes(k) && !fieldFilled(k)
      ? "bg-amber-50 border-amber-400 ring-2 ring-amber-200"
      : "bg-muted/30";
  const addressValid = deliveryMode !== 'entrega'
    || (manualMode
        ? (manualRequiredOk && (feeType !== "neighborhood" || !!neighborhoodFeeOk))
        // Por bairro é EXCLUSIVO: só o gate dele decide. Deixar no || geral
        // dava escape — a alternativa genérica (rua+nº+bairro && lat/lng)
        // passava por fora dos obrigatórios assim que a busca preenchia o
        // endereço (pego pelo Pedro em 08/08: referência obrigatória vazia e o
        // pedido finalizou).
        : (autocompleteRequiredOk && (feeType === "neighborhood"
            ? (nbRequiredOk && nbBaseOk && bairroEscolhido)
            : (distanceFeeResolved || (feeType === "on_request" && baseAddrOk) || (baseAddrOk && ((address.lat != null && address.lng != null) || cidadeCepOk))))));

  // O valor da entrega POR BAIRRO só aparece com o bairro escolhido E o
  // endereço completo — o mesmo gate que libera o envio (decisão do Pedro,
  // 02/10/2026). Antes disso a tela mostra o total SEM a entrega ("Total
  // parcial · + entrega"), senão o cliente trocava de bairro olhando o total.
  // Só exibição: cupom, acréscimo, envio e servidor seguem com a taxa real.
  // POR DISTÂNCIA (Pedro, 21/10/2026: «não pode falar que é grátis primeiro»): igual, até a taxa ser cotada — sem
  // cotação a taxa crua é 0 e o resumo dizia «Grátis». Basta a cotação, sem esperar o endereço completo. Frete grátis
  // pelo limiar já atingido continua «Grátis», que é verdade (o `freeApplied` só acende com taxa > 0, daí a conta aqui).
  const limiarFreteGratis = Number((store as any)?.freeDeliveryAbove);
  const freteGratisAtingido = Number.isFinite(limiarFreteGratis) && limiarFreteGratis > 0 &&
    ((store as any)?.freeDeliveryBeforeCoupon === true ? total : cupomMath.netProducts) >= limiarFreteGratis;
  const taxaOculta =
    deliveryMode === "entrega" && !feePending && !noDeliveryBlocked &&
    (feeType === "neighborhood"
      ? !(dynamicFee && addressValid)
      : feeType === "distance" && (dynamicFee === null || feeLoading) && !freteGratisAtingido);
  const mixedTela = taxaOculta && paymentMixed ? calcularMisto(0) : mixed;
  const mixedCashTela = mixedTela
    ? (mixedTela.m1 === "Dinheiro" ? mixedTela.a1 : mixedTela.m2 === "Dinheiro" ? mixedTela.a2 : 0)
    : 0;
  const couponLineTela = taxaOculta ? Math.round(cupomMath.productDiscount * 100) / 100 : couponLineAmount;
  const totalTela = taxaOculta
    ? computeOrderTotal({
        subtotal: total,
        discount: cupomMath.productDiscount + rewardMath.productDiscount,
        deliveryFee: 0,
        surcharge: paymentMixed && mixedTela
          ? mixedTela.surcharge
          : computeCardSurcharge((store as any)?.cardSurchargeConfig, paymentMethod, totalAfterCoupon, 0),
      })
    : finalTotal;

  // Pedido mínimo do BAIRRO (03/10/2026): aparece no mesmo momento da taxa
  // (taxaOculta), decisão do Pedro. O geral vale sempre e, com os dois, vale
  // o maior — então aqui só conta o do bairro MAIOR que o geral (o geral já é
  // cobrado na vitrine e no envio). Base = produtos, sem a entrega, igual ao
  // servidor (routes/catalog/orders.ts), comparado em centavos.
  const minimoGeral = parseFloat((store as any)?.minOrderValue ?? "0") || 0;
  const bairroDaTaxa = deliveryMode === "entrega" && feeType === "neighborhood" && dynamicFee && !dynamicFee.noDelivery &&
    selectedNeighborhood && selectedNeighborhood !== "__other__"
    ? neighborhoodList.find(n => normalizeNeighborhood(n.name) === normalizeNeighborhood(selectedNeighborhood))
    : undefined;
  const minimoDoBairro = bairroDaTaxa && bairroDaTaxa.minOrder > minimoGeral ? bairroDaTaxa.minOrder : 0;
  const faltaParaMinimoDoBairro = minimoDoBairro > 0 && Math.round(total * 100) < Math.round(minimoDoBairro * 100)
    ? Math.round((minimoDoBairro - total) * 100) / 100
    : 0;

  useEffect(() => {
    if (!highlightField) return;
    if (highlightField === "delivery" && deliveryMode) {
      setHighlightField(null);
      return;
    }
    if (highlightField === "encomenda" && encomendaCompleta) {
      setHighlightField(null);
      return;
    }
    if (highlightField === "addressForm" && (deliveryMode !== "entrega" || savedAddresses.length === 0 || addressFormMode === "hidden")) {
      setHighlightField(null);
      return;
    }
    if (highlightField === "address" && addressValid) {
      setHighlightField(null);
      return;
    }
    if (highlightField === "noDelivery" && !noDeliveryBlocked) {
      setHighlightField(null);
      return;
    }
    if (highlightField === "feeLoading" && !feeNotResolved) {
      setHighlightField(null);
      return;
    }
    if (highlightField === "payment" && paymentMethod) {
      setHighlightField(null);
      return;
    }
    if (highlightField === "changeFor") {
      if (paymentMethod !== "Dinheiro" || needsChange !== true) {
        setHighlightField(null);
        return;
      }
      const digits = changeFor.replace(/\D/g, "");
      if (digits && parseInt(digits, 10) / 100 >= finalTotal) {
        setHighlightField(null);
      }
    }
    if (highlightField === "gift" && ehPresente !== null) {
      setHighlightField(null);
      return;
    }
    if (highlightField === "giftName" && (ehPresente !== true || quemRecebe.trim())) {
      setHighlightField(null);
    }
  }, [highlightField, deliveryMode, addressFormMode, savedAddresses.length, addressValid, noDeliveryBlocked, feeNotResolved, paymentMethod, needsChange, changeFor, finalTotal, encData, encHora, encTurno, ehPresente, quemRecebe]);

  const scrollAndHighlight = (field: Exclude<HighlightField, null>, ref: React.RefObject<HTMLElement | null>, focusInput?: HTMLInputElement | null) => {
    setHighlightField(field);
    if (focusInput) {
      try { focusInput.focus({ preventScroll: false }); } catch { focusInput.focus(); }
    } else {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const validateOrder = (): boolean => {
    if (precisaEncomenda && !encomendaCompleta) {
      scrollAndHighlight("encomenda", encomendaCardRef);
      toast({
        title: modoEnc === "turno" ? "Escolha a data e o turno da encomenda" : modoEnc === "data" ? "Escolha a data da encomenda" : "Escolha a data e o horário da encomenda",
        variant: "destructive",
      });
      return false;
    }
    if (!deliveryMode) {
      scrollAndHighlight("delivery", deliveryCardRef);
      toast({ title: "Escolha como deseja receber o pedido", variant: "destructive" });
      return false;
    }
    if (deliveryMode === "entrega" && entregaSemSaida) {
      // Loja só de entrega sem bairro configurado: não há endereço a completar.
      scrollAndHighlight("delivery", deliveryCardRef);
      toast({ title: "Esta loja ainda não configurou os bairros de entrega. Fale com a loja pelo WhatsApp.", variant: "destructive" });
      return false;
    }
    if (deliveryMode === "entrega" && savedAddresses.length > 0 && addressFormMode !== "hidden") {
      scrollAndHighlight("addressForm", addressBlockRef);
      toast({ title: "Salve ou cancele o endereço antes de continuar", variant: "destructive" });
      return false;
    }
    if (!addressValid) {
      // Rola até o 1º campo faltante e foca nele (mesmo padrão da forma de
      // pagamento). Os ids addr-f-<campo> existem nos três formulários; se o
      // campo não estiver no DOM (ex.: painel da busca ainda fechado), cai no
      // scroll do bloco de endereço.
      setFlagMissing(missingAddressKeys);
      const el = missingAddressKeys.length > 0
        ? document.getElementById(`addr-f-${missingAddressKeys[0]}`)
        : null;
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => (el as HTMLElement).focus?.(), 350);
      } else {
        scrollAndHighlight("address", addressBlockRef);
      }
      toast({ title: addressMissingMsg, variant: "destructive" });
      return false;
    }
    if (noDeliveryBlocked) {
      scrollAndHighlight("noDelivery", addressBlockRef);
      toast({ title: "Não entregamos nesse endereço. Escolha retirada no local ou outro endereço.", variant: "destructive" });
      return false;
    }
    if (feeNotResolved) {
      scrollAndHighlight("feeLoading", addressBlockRef);
      toast({ title: feeNotResolvedMsg, variant: "destructive" });
      return false;
    }
    const minOrderVal = parseFloat((store as any)?.minOrderValue ?? "0") || 0;
    if (minOrderVal > 0 && total < minOrderVal) {
      toast({ title: `Pedido mínimo: ${formatCurrency(minOrderVal)}`, description: `Faltam ${formatCurrency(minOrderVal - total)}.`, variant: "destructive" });
      return false;
    }
    if (faltaParaMinimoDoBairro > 0 && bairroDaTaxa) {
      minimoDoBairroRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      toast({ title: `Pedido mínimo para entrega em ${bairroDaTaxa.name}: ${formatCurrency(minimoDoBairro)}`, description: `Faltam ${formatCurrency(faltaParaMinimoDoBairro)}.`, variant: "destructive" });
      return false;
    }
    if (paymentMixed) {
      if (!mixed || !mixed.m1 || !mixed.m2 || mixed.m1 === mixed.m2) {
        scrollAndHighlight("payment", paymentCardRef);
        toast({ title: "Escolha duas formas de pagamento diferentes", variant: "destructive" });
        return false;
      }
      if (mixed.a1 <= 0 || mixed.a2 <= 0) {
        scrollAndHighlight("payment", paymentCardRef);
        toast({
          title: "Informe quanto vai pagar na primeira forma",
          description: `O valor precisa ser maior que zero e menor que o total (${formatCurrency(finalTotal)}).`,
          variant: "destructive",
        });
        return false;
      }
    } else if (!paymentMethod) {
      scrollAndHighlight("payment", paymentCardRef);
      toast({ title: "Escolha a forma de pagamento", variant: "destructive" });
      return false;
    }
    if (needsChange === true) {
      const digits = changeFor.replace(/\D/g, "");
      if (!digits) {
        scrollAndHighlight("changeFor", paymentCardRef, changeForInputRef.current);
        toast({ title: "Informe o valor do troco", variant: "destructive" });
        return false;
      }
      const changeForValue = parseInt(digits, 10) / 100;
      // No misto o troco é sobre a PARTE em dinheiro, não o total.
      const trocoBase = paymentMixed && mixedCashAmount > 0 ? mixedCashAmount : finalTotal;
      if (changeForValue < trocoBase) {
        scrollAndHighlight("changeFor", paymentCardRef, changeForInputRef.current);
        toast({ title: `O valor do troco deve ser ao menos ${formatCurrency(trocoBase)}`, variant: "destructive" });
        return false;
      }
    }
    // Presente: o card fica abaixo do pagamento, então o gate vem depois do troco.
    if (pedePresente && ehPresente === null && presenteObrigatorio) {
      scrollAndHighlight("gift", giftCardRef);
      toast({ title: "Escolha Não ou Sim para continuar.", variant: "destructive" });
      return false;
    }
    if (pedePresente && ehPresente === true && !quemRecebe.trim()) {
      scrollAndHighlight("giftName", giftCardRef, giftNameInputRef.current);
      toast({ title: "Informe quem vai receber o presente para continuar.", variant: "destructive" });
      return false;
    }
    setHighlightField(null);
    return true;
  };

  // Endereço que vai junto na validação do cupom — cupom de ação promocional
  // pode exigir região. Só manda quando JÁ existe endereço: sem isso o servidor
  // responde "escolha o endereço" em vez de "não vale aqui".
  const couponAddressPayload = () => {
    if (deliveryMode === "retirada" || deliveryMode === "combinar") return { deliveryMode };
    if (deliveryMode !== "entrega") return {};
    const temEndereco = !!(address.street || address.neighborhood || address.zipCode);
    if (!temEndereco) return {};
    return {
      deliveryMode: "entrega" as const,
      address: {
        cep: address.zipCode || null,
        state: address.state || null,
        city: address.city || null,
        neighborhood: address.neighborhood || null,
        street: address.street || null,
        number: address.number || null,
        complement: address.complement || null,
        reference: address.reference || null,
      },
    };
  };

  const applyCoupon = async (codeArg?: string) => {
    const code = (codeArg ?? couponInput).trim();
    if (!code) {
      setCouponError("Informe o código do cupom.");
      return;
    }
    setCouponChecking(true);
    setCouponError(null);
    try {
      const resp = await customerCustomFetch<{
        valid: boolean;
        code: string;
        discountType: "percent" | "fixed" | "free_delivery";
        discountValue: number;
        discount: number;
      }>(`/api/catalog/${slug}/coupons/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code,
          subtotal: total,
          ...(unitId ? { unitId } : {}),
          ...couponAddressPayload(),
          // Cupom de frete grátis: o servidor recusa se não há taxa a zerar.
          ...(deliveryMode === "entrega" ? { deliveryFee, feePending } : {}),
        }),
      });
      const novo = { code: resp.code, discountType: resp.discountType, discountValue: resp.discountValue };
      setAppliedCoupons((prev) => stackable
        ? (prev.some((c) => c.code === novo.code) ? prev : [...prev, novo])
        : [novo]);
      setCouponInput("");
    } catch (e) {
      if (!stackable) setAppliedCoupons([]);
      setCouponError(extractApiErrorMessage(e, "Não foi possível validar o cupom — tente de novo."));
    } finally {
      setCouponChecking(false);
    }
  };

  // Trocar de endereço depois de aplicar o cupom tem que revalidar: senão o
  // cliente vê o desconto na tela e só descobre que perdeu na hora de enviar o
  // pedido, quando a checagem autoritativa do servidor derruba.
  const enderecoFingerprint = [
    deliveryMode,
    address.zipCode,
    address.state,
    address.city,
    address.neighborhood,
    address.street,
    address.number,
    address.complement,
    address.reference,
  ].join("|");
  const revalidatedForRef = useRef<string | null>(null);
  const codesAplicados = appliedCoupons.map((c) => c.code).join("+");
  useEffect(() => {
    if (appliedCoupons.length === 0) {
      revalidatedForRef.current = null;
      return;
    }
    const chave = `${codesAplicados}@${enderecoFingerprint}@${catalogBump}`;
    if (revalidatedForRef.current === chave) return;
    revalidatedForRef.current = chave;
    let cancelado = false;
    (async () => {
      // Revalida cada um; só o que falhar cai (o resto fica de pé).
      for (const cup of appliedCoupons) {
        try {
          await customerCustomFetch(`/api/catalog/${slug}/coupons/validate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              code: cup.code,
              subtotal: total,
              ...(unitId ? { unitId } : {}),
              ...couponAddressPayload(),
              ...(deliveryMode === "entrega" ? { deliveryFee, feePending } : {}),
            }),
          });
        } catch (e) {
          if (cancelado) return;
          setAppliedCoupons((prev) => prev.filter((c) => c.code !== cup.code));
          setCouponError(extractApiErrorMessage(e, `O cupom ${cup.code} não vale mais para este endereço.`));
        }
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codesAplicados, enderecoFingerprint, catalogBump]);

  applyCouponRef.current = applyCoupon;


  const removeCoupon = (code?: string) => {
    setAppliedCoupons((prev) => (code ? prev.filter((c) => c.code !== code) : []));
    setCouponError(null);
    setCouponInput("");
  };

  const handlePlaceOrder = async () => {
    if (!customer) {
      setLoginOpen(true);
      return;
    }
    if (lojaDesativada) {
      toast({
        title: "Pedidos pausados",
        description: "A loja não está recebendo pedidos agora. Tente mais tarde.",
        variant: "destructive",
      });
      return;
    }
    if (closedAndBlocked) {
      toast({
        title: "A loja está fechada",
        description: "Esta loja não aceita pedidos fora do horário de funcionamento.",
        variant: "destructive",
      });
      return;
    }
    if (!validateOrder()) return;

    // Loja pede data de nascimento e o cliente ainda não tem → coleta aqui.
    const needBirthDate = !!(store as any)?.askBirthDate && !customer.birthDate;
    if (needBirthDate && (store as any)?.birthDateRequired && !birthDate) {
      toast({ title: "Informe sua data de nascimento para continuar", variant: "destructive" });
      return;
    }

    try {
      const orderData: CreateOrderRequest = {
        items: items.map(i => ({
          productId: i.product.id,
          quantity: i.quantity,
          notes: i.notes,
          ...(i.selectedOptions ? { selectedOptions: i.selectedOptions } : {}),
          ...(i.removedIngredients ? { removedIngredients: i.removedIngredients } : {}),
          ...(agendaDoItem(i).scheduledDate ? { scheduledDate: agendaDoItem(i).scheduledDate } : {}),
          ...(agendaDoItem(i).scheduledTime ? { scheduledTime: agendaDoItem(i).scheduledTime } : {}),
          ...(agendaDoItem(i).scheduledShift ? { scheduledShift: agendaDoItem(i).scheduledShift } : {}),
          ...((i as any).weightKg != null ? { weightKg: (i as any).weightKg } : {}),
        })) as CreateOrderRequest["items"],
        paymentMethod: paymentMixed ? "Misto" : paymentMethod,
        deliveryMode,
        ...(deliveryMode === 'retirada' && pickupMethod ? ({ pickupMethod } as object) : {}),
        notes: notes.trim() || undefined,
        ...((paymentMethod === 'Dinheiro' || (paymentMixed && mixedCashAmount > 0)) && needsChange === true && changeFor
          ? { cashReceived: parseInt(changeFor.replace(/\D/g, ""), 10) / 100 }
          : {}),
        ...(deliveryMode === 'entrega'
          ? (selectedAddressId && addressFormMode === "hidden"
              ? { addressId: selectedAddressId }
              : { newAddress: { ...address } as CreateOrderRequest["newAddress"] })
          : {}),
        ...(unitId ? { unitId } : {}),
      };

      const order = await createOrderMutation.mutateAsync({
        slug,
        data: {
          ...orderData,
          // Taxa exibida no checkout — o server rejeita (DELIVERY_FEE_CHANGED)
          // se a autoritativa divergir >R$0,05: nunca cobrar diferente do visto.
          ...(deliveryMode === 'entrega' ? { quotedDeliveryFee: deliveryFee } : {}),
          ...(needBirthDate && birthDate ? { birthDate } : {}),
          // Sem resposta (só quando é opcional) não manda nada: o pedido sai como sempre.
          ...(pedePresente && ehPresente !== null
            ? { isGift: ehPresente, giftRecipient: ehPresente ? quemRecebe.trim() : null }
            : {}),
          ...(appliedCoupons.length === 1 ? { couponCode: appliedCoupons[0]!.code } : {}),
          ...(appliedCoupons.length > 1 ? { couponCodes: appliedCoupons.map((c) => c.code) } : {}),
          ...(paymentMixed && mixed
            ? { paymentParts: [{ method: mixed.m1, amount: mixed.a1 }, { method: mixed.m2, amount: mixed.a2 }] }
            : {}),
          ...(isCrediarioMethod && !paymentMixed ? { crediarioInstallments } : {}),
          ...(premiosNoPedido.length > 0
            ? {
                loyaltyRewards: premiosNoPedido.map((u) => ({
                  rewardId: u.rewardId,
                  ...(u.type === "cashback" ? { points: u.points } : {}),
                })),
              }
            : {}),
          ...(indicacao ? { referralCode: indicacao } : {}),
        } as any,
      });

      if (deliveryMode === 'entrega' && order.addressId) {
        try {
          await customerCustomFetch(`/api/catalog/${slug}/addresses/${order.addressId}`, {
            method: "PUT",
            body: JSON.stringify({ isDefault: true }),
          });
        } catch (e) {
          console.error("Falha ao marcar endereço como padrão", e);
        }
      }

      clearCart();
      if (indicacao) {
        try { localStorage.removeItem(chaveIndicacao(slug)); } catch {}
      }
      if (loyaltyOn) void refetchLoyalty();

      subscribeCustomerPush(store?.id);
      
      // Sem código na mensagem que o cliente envia pra loja — a numeração do
      // pedido é interna (número do dia), não exposta aqui.
      let msg = `*Novo Pedido*\n\n`;
      // A hora do servidor, no fuso da loja: o cliente que copia e reenvia a mensagem de outro dia não confunde a loja
      // (Paiva do Copão, 27/10/2026: chegou no WhatsApp o pedido de 23/10, que não estava em Pedidos). Opção da loja,
      // desligada por padrão (Pedro, 01/11/2026: «Quero desligado como default e a loja que quiser, liga»).
      if ((store as { orderMessageShowDate?: boolean } | undefined)?.orderMessageShowDate === true) {
        const feitoEm = (order as { createdAt?: string | null }).createdAt ?? new Date().toISOString();
        msg += `*Pedido feito em:* ${formatBRDate(feitoEm)} às ${formatBRTime(feitoEm)}\n`;
      }
      msg += `*Cliente:* ${customer.firstName}\n`;
      if (pedePresente && ehPresente === true && quemRecebe.trim()) msg += `*Presente para:* ${quemRecebe.trim()}\n`;
      msg += `*Modalidade:* ${deliveryMode === 'combinar' ? 'A combinar' : deliveryMode.toUpperCase()}${deliveryMode === 'retirada' && pickupMethod ? ` — ${pickupMethod}` : ''}\n`;
      if (deliveryMode === 'entrega') {
        if (manualMode) {
          const parts: string[] = [];
          if (manualFields.street.enabled && address.street) {
            const streetPart = address.street + (manualFields.number.enabled && address.number ? `, ${address.number}` : '');
            parts.push(streetPart);
          } else if (manualFields.number.enabled && address.number) {
            parts.push(address.number);
          }
          if (manualFields.complement.enabled && address.complement) parts.push(address.complement);
          if (manualFields.neighborhood.enabled && address.neighborhood) parts.push(address.neighborhood);
          const cityState = [
            manualFields.city.enabled ? address.city : "",
            manualFields.state.enabled ? address.state : "",
          ].filter(Boolean).join("/");
          if (cityState) parts.push(cityState);
          msg += `*Endereço:* ${parts.join(' - ')}\n`;
          if (manualFields.cep.enabled && address.zipCode) {
            msg += `*CEP:* ${address.zipCode.replace(/^(\d{5})(\d{3})$/, "$1-$2")}\n`;
          }
          if (manualFields.reference.enabled && address.reference) {
            msg += `*Referência:* ${address.reference}\n`;
          }
        } else if (feeType === "neighborhood" && !neighborhoodMeta.acceptUnlisted) {
          msg += `*Endereço:* ${address.street}, ${address.number}${address.complement ? ` - ${address.complement}` : ''}\n`;
          if (dynamicFee?.neighborhood) {
            msg += `*Bairro:* ${dynamicFee.neighborhood}\n`;
          }
        } else {
          msg += `*Endereço:* ${address.street}, ${address.number}${address.complement ? ` - ${address.complement}` : ''} - ${address.neighborhood}${address.city ? `, ${address.city}` : ''}${address.zipCode ? ` - CEP: ${address.zipCode}` : ''}\n`;
        }
        // Referência vale em qualquer modo — o campo agora também aparece na
        // busca do Google quando a loja habilita.
        if (!manualMode && manualFields.reference.enabled && address.reference) {
          msg += `*Referência:* ${address.reference}\n`;
        }
        if (!manualMode && feeType === "distance" && dynamicFee?.distanceKm) {
          // Com vírgula, como o resto da mensagem (Pedro, 15/10/2026: «~3.6 km» → «~3,6 km»).
          msg += `*Distância:* ~${String(dynamicFee.distanceKm).replace(".", ",")} km\n`;
        }
        // Ponto do GPS/pino (ou da busca): o texto pode ser ambíguo (CEP que
        // cobre várias quadras) e o lojista quer abrir no Maps. Fora do
        // "digitar manualmente": entrar nele NÃO zera o ponto, e quem usou o
        // GPS e depois digitou outro endereço mandaria o lugar errado. A loja pode desligar o link («Receber o link da
        // localização no pedido», 23/10/2026): só a linha sai; o ponto continua gravado no pedido.
        if (!userManualEntry && address.lat != null && address.lng != null && (store as any)?.locationLinkInOrder !== false) {
          msg += `*Localização:* https://www.google.com/maps/search/?api=1&query=${address.lat.toFixed(6)},${address.lng.toFixed(6)}\n`;
        }
      }
      // Data da encomenda UMA vez quando é a mesma em todos os itens agendados (data única, ou a mesma escolhida
      // produto a produto); com datas diferentes continua uma linha por item.
      const dataComum = dataUnicaDosItens(items.map(agendaDoItem));
      if (dataComum) {
        msg += `*Encomenda para:* ${encomendaPorExtenso(dataComum.data, dataComum.hora, dataComum.turno)}\n`;
      }
      msg += `\n*Itens:*\n`;
      const showGroupTitles = (store as { showGroupTitles?: boolean } | undefined)?.showGroupTitles !== false;
      // Item COM adicional e quantidade > 1 sai em um bloco por unidade
      // ("1 de 2"): "2x Burger + 2x carne" não diz se são duas carnes num
      // lanche só ou uma em cada. Sem adicional continua junto ("4x Açaí").
      // Desconto por quantidade POR UNIDADE (risco unidade a unidade, como no
      // carrinho — pedido do Pedro, 09/08). idx = posição da linha em `items`.
      const qtyUnitDiscs = quantityPromoUnitDiscountsByLine(items, "link");
      const linhas = items.flatMap((i, idx) => {
        const n = Math.max(1, i.quantity || 1);
        return n > 1 && i.selectedOptions && i.selectedOptions.length > 0
          ? Array.from({ length: n }, (_, k) => ({ i, idx, k, unidade: `${k + 1} de ${n}` }))
          : [{ i, idx, k: -1, unidade: null as string | null }];
      });
      linhas.forEach(({ i, idx, k, unidade }, itemIdx) => {
        const unit = (i as any).unitPrice !== undefined ? (i as any).unitPrice : Number(i.product.price);
        const original = (i as any).originalUnitPrice !== undefined ? (i as any).originalUnitPrice : unit;
        const hasDisc = ((i as any).discountAmount || 0) > 0;
        const qtd = unidade ? 1 : i.quantity;
        const pesoLabel = (i as any).weightKg != null
          ? ` (${(i.product as any).weightUnit === "ml" ? `${Math.round((i as any).weightKg * 1000)} ml` : (i as any).weightKg < 1 ? `${Math.round((i as any).weightKg * 1000)} g` : `${String((i as any).weightKg).replace(".", ",")} kg`})`
          : "";
        const rotulo = unidade ? `${i.product.name}${pesoLabel} (${unidade})` : `${i.quantity}x ${i.product.name}${pesoLabel}`;
        // Desconto de quantidade desta LINHA da mensagem: da unidade k (bloco)
        // ou da linha inteira somada.
        const unitDiscs = qtyUnitDiscs[idx] || [];
        const qtyDisc = unidade
          ? (unitDiscs[k] || 0)
          : Math.round(unitDiscs.reduce((s, d) => s + d, 0) * 100) / 100;
        // Onde o risco entra: no preço-base quando ele existe; com base zerada
        // e UM adicional com preço, risca no adicional (caso Blusa+tamanho).
        const pricedOpts = (i.selectedOptions || []).filter(o => (o.price || 0) > 0);
        const strikeOnOption = qtyDisc > 0 && unit * qtd <= 0 && pricedOpts.length === 1;
        const baseShown = unit * qtd;
        if (hasDisc) {
          msg += `${rotulo} - ~${formatCurrency(original * qtd)}~ ${formatCurrency(baseShown)} _(promo)_\n`;
        } else if (qtyDisc > 0 && !strikeOnOption) {
          // Sem adicional (ou base > 0): risco no valor da linha/unidade.
          msg += `${rotulo} - ~${formatCurrency(baseShown)}~ ${formatCurrency(Math.max(0, Math.round((baseShown - qtyDisc) * 100) / 100))} _(promo)_\n`;
        } else if (baseShown > 0) {
          msg += `${rotulo} - ${formatCurrency(baseShown)}\n`;
        } else {
          // Preço-base zerado: o valor mora nos adicionais — não imprime "R$ 0,00".
          msg += `${rotulo}\n`;
        }
        // Pacote de escolhas: a conta do item, com o valor dos extras (a linha do item mostra só o preço-base).
        const prepMsg = prepCountFor((i.product as any).optionGroups, (i.product as any).prepCount, i.selectedOptions);
        // «Cada lista conta separado» (28/10/2026): a conta de cada lista vai no título dela, com cada adicional embaixo do
        // seu copo, e os títulos saem mesmo com a loja escondendo os dos grupos (senão o copo se perde).
        const contasDasListas = prepMsg?.listas ? gruposComConta(i.selectedOptions, prepMsg, qtd) : null;
        if (prepMsg && !contasDasListas) {
          msg += `   ${prepCountLine(prepMsg)}${prepMsg.extrasValue > 0 ? ` ${formatCurrency(prepMsg.extrasValue * qtd)}` : ""}\n`;
        }
        const tituloDaLista = (titulo: string) => {
          const g = contasDasListas?.find((x) => x.titulo === titulo);
          return g?.conta ? ` ${g.conta}${g.extrasValue > 0 ? ` ${formatCurrency(g.extrasValue * qtd)}` : ""}` : "";
        };
        let hadGroupTitle = false;
        // Promoção nas opções (produto de preço base 0; em % desde 23/10/2026, valor fixo desde 24/10): a opção com
        // desconto sai com o preço do cardápio riscado, no formato da promoção por quantidade. O extra, que fica cheio,
        // sai como sempre.
        const promoNasOpcoes = promocaoNasOpcoes(i.product, "link") != null;
        const precoCheio = (titulo: string, nome: string): number | null => {
          for (const gr of ((i.product as any).optionGroups || []) as Array<{ name?: string; options?: Array<{ name: string; price?: number }> }>) {
            if (String(gr?.name ?? "").trim() !== titulo) continue;
            const o = gr.options?.find((x) => x.name === nome);
            if (o) return Number(o.price) || 0;
          }
          return null;
        };
        if (i.selectedOptions && i.selectedOptions.length > 0) {
          groupOptionsByTitle(i.selectedOptions, qtd).forEach(grp => {
            if (grp.title && (showGroupTitles || contasDasListas)) {
              hadGroupTitle = true;
              msg += `\n   *${grp.title}:*${tituloDaLista(grp.title)}\n`;
            }
            grp.options.forEach(g => {
              const optTotal = g.price * g.count;
              const cheio = promoNasOpcoes ? precoCheio(grp.title, g.name) : null;
              if (strikeOnOption && g.price > 0) {
                msg += `   + ${g.count > 1 ? `${g.count}x ` : ''}${g.name} ~${formatCurrency(optTotal)}~ ${formatCurrency(Math.max(0, Math.round((optTotal - qtyDisc) * 100) / 100))} _(promo)_\n`;
              } else if (cheio != null && cheio > g.price + 0.005) {
                msg += `   + ${g.count > 1 ? `${g.count}x ` : ''}${g.name} ~${formatCurrency(cheio * g.count)}~ ${formatCurrency(optTotal)} _(promo)_\n`;
              } else {
                msg += `   + ${g.count > 1 ? `${g.count}x ` : ''}${g.name}${g.price > 0 ? ` ${formatCurrency(optTotal)}` : ''}\n`;
              }
            });
          });
        }
        // A lista do pacote que ficou sem escolha também sai, com «0 de 2 no preço».
        for (const g of contasDasListas ?? []) {
          if (g.opcoes.length === 0 && g.conta) { hadGroupTitle = true; msg += `\n   *${g.titulo}:* ${g.conta}\n`; }
        }
        if (i.removedIngredients && i.removedIngredients.length > 0) {
          msg += `   *RETIRAR: ${i.removedIngredients.join(', ')}*\n`;
        }
        const agendaItem = agendaDoItem(i);
        if (!dataComum && agendaItem.scheduledDate) {
          msg += `   Encomenda para: ${encomendaPorExtenso(agendaItem.scheduledDate, agendaItem.scheduledTime, agendaItem.scheduledShift)}\n`;
        }
        if (i.notes) msg += `   Obs: ${i.notes}\n`;
        // Item com blocos de título fica alto — respiro antes do próximo item.
        if (hadGroupTitle && itemIdx < linhas.length - 1) msg += `\n`;
      });
      for (const u of premiosNoPedido) {
        if (u.type === "gift") msg += `1x Brinde: ${u.name} _(${u.points} pontos)_\n`;
      }
      // Promo por quantidade: o desconto sai RISCADO na própria linha/unidade
      // acima (preferência do Pedro, 09/08) — sem linha agregada na mensagem.
      // O cupom sai no bloco de fechamento (junto do Subtotal), não aqui.
      if (paymentMixed && mixed) {
        msg += `\n*Pagamento:* Misto\n`;
        msg += `   ${mixed.m1}: ${formatCurrency(mixed.a1)}\n`;
        msg += `   ${mixed.m2}: ${formatCurrency(mixed.a2)}\n`;
      } else if (isCrediarioMethod && selectedCrediarioCfg) {
        const nCred = Math.max(1, crediarioInstallments);
        const jurosCred = crediarioInterestFor(selectedCrediarioCfg, nCred);
        const totalPrazoCred = finalTotal * (1 + jurosCred / 100);
        msg += `\n*Pagamento:* ${paymentMethod} (${nCred}x de ${formatCurrency(totalPrazoCred / nCred)}${jurosCred > 0 ? ", juros incluídos" : ""})\n`;
      } else {
        msg += `\n*Pagamento:* ${paymentMethod}\n`;
      }
      if (paymentMethod === 'Dinheiro' || (paymentMixed && mixedCashAmount > 0)) {
        if (needsChange === false) msg += `*Troco:* Não precisa\n`;
        else if (needsChange === true && changeFor) {
          const changeForValue = parseInt(changeFor.replace(/\D/g, ""), 10) / 100;
          const changeAmount = changeForValue - (paymentMixed && mixedCashAmount > 0 ? mixedCashAmount : finalTotal);
          msg += `*Troco para:* ${formatCurrency(changeForValue)}\n`;
          if (changeAmount > 0) msg += `*Troco a devolver:* ${formatCurrency(changeAmount)}\n`;
        }
        else if (needsChange === true) msg += `*Troco:* Sim\n`;
      }
      // Bloco de fechamento: Subtotal → descontos → frete/acréscimo → TOTAL,
      // pra conta fechar visualmente. Com cupom o bloco aparece SEMPRE (mesmo
      // retirada sem acréscimo) — o desconto precisa de um subtotal ao lado.
      const temCupom = couponLineAmount > 0;
      const temPremio = premiosNoPedido.length > 0;
      if (deliveryMode === 'entrega' || cardSurcharge > 0 || temCupom || temPremio) {
        msg += `\n*Subtotal:* ${formatCurrency(grossSubtotal)}\n`;
        if (qtyPromoTotal > 0) {
          msg += `*Promoções:* -${formatCurrency(qtyPromoTotal)}\n`;
        }
        if (temCupom) {
          for (const cup of cuponsSemFrete) {
            msg += `*Cupom (${cup.code}):* -${formatCurrency(economiaDoCupom(cup))}\n`;
          }
        }
        for (const { u, d } of premiosComDesconto) {
          msg += `*Pontos (${u.name}):* -${formatCurrency(d)}\n`;
        }
        if (deliveryMode === 'entrega') {
          if (feePending) {
            msg += `*Frete:* calculado após pedido\n`;
          } else if (premioFreteZeraTaxa) {
            msg += `*Entrega:* ~${formatCurrency(deliveryFee)}~ Grátis! (pontos)\n`;
          } else if (cupomFreteZeraTaxa) {
            const cupomFrete = appliedCoupons.find((c) => c.discountType === "free_delivery");
            msg += `*Entrega:* ~${formatCurrency(deliveryFee)}~ Grátis! (cupom ${cupomFrete?.code ?? ""})\n`;
          } else if (deliveryFreeApplied) {
            msg += `*Entrega:* ~${formatCurrency(dynamicFee?.fee ?? parseFloat((store as any)?.deliveryFee ?? "0"))}~ Grátis!\n`;
          } else {
            msg += `*Entrega:* ${deliveryFee > 0 ? formatCurrency(deliveryFee) : 'Grátis'}\n`;
          }
        }
      }
      if (cardSurcharge > 0) {
        msg += `*Acréscimo (${paymentMethod}):* ${formatCurrency(cardSurcharge)}\n`;
      }
      if (feePending) {
        msg += `\n*Total parcial:* ${formatCurrency(finalTotal)}\n`;
        msg += `_(Frete a calcular e somar após o pedido)_\n`;
      } else {
        msg += `\n*TOTAL:* ${formatCurrency(finalTotal)}\n`;
      }
      if (temPremio) {
        msg += `*Pontos usados:* ${premiosNoPedido.reduce((s, u) => s + u.points, 0)}\n`;
      }
      if (notes.trim()) msg += `\n*Observações:* ${notes.trim()}\n`;

      const waLink = `https://api.whatsapp.com/send?phone=${store?.whatsappNumber}&text=${encodeURIComponent(msg)}`;
      window.location.href = waLink;

    } catch (e: any) {
      // Taxa recalculada no server (divergiu da exibida): força re-cotação pra
      // UI mostrar o valor novo antes do cliente reenviar.
      if (e?.data?.error?.code === "DELIVERY_FEE_CHANGED") {
        distanceFetchRef.current = "";
        setRequoteTick(t => t + 1);
      }
      const msg = extractApiErrorMessage(e, "Não foi possível gerar o pedido — tente de novo em instantes.");
      toast({ title: msg, variant: "destructive" });
    }
  };

  if (!store || items.length === 0) return null;

  if (customerLoading) {
    return (
      <div className="min-h-[100svh] flex items-center justify-center bg-gray-100">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!customer) {
    return (
      <>
        <div className="min-h-[100svh] bg-gray-100 flex items-center justify-center p-4">
          <div className="bg-card max-w-md w-full p-8 rounded-3xl shadow-xl border border-border/50 text-center space-y-4">
            <BrandTile size="lg" className="mx-auto" />
            <div>
              <h2 className="text-2xl font-bold font-display">Para quem é o pedido?</h2>
              <p className="text-muted-foreground mt-2">Confirme seu WhatsApp para finalizar a compra.</p>
            </div>
            <Button className="w-full h-12" onClick={() => setLoginOpen(true)}>
              Entrar com WhatsApp
            </Button>
          </div>
        </div>
        <CustomerLoginModal
          open={loginOpen}
          onOpenChange={setLoginOpen}
          dismissible={false}
          askBirthDate={(store as any)?.askBirthDate}
          birthDateRequired={(store as any)?.birthDateRequired}
          storeSlug={slug}
          loyaltyEnabled={(store as any)?.loyaltyEnabled === true}
        />
      </>
    );
  }

  return (
    <div className="min-h-[100svh] bg-gray-100 max-w-3xl mx-auto flex flex-col relative">
      <header className="bg-card px-4 py-4 flex items-center gap-4 sticky top-0 z-10 shadow-sm">
        <Link href={`/loja/${slug}`} className="p-2 -ml-2 rounded-full hover:bg-muted text-muted-foreground"><ChevronLeft className="w-6 h-6"/></Link>
        <h1 className="text-xl font-bold font-display">Finalizar Pedido</h1>
      </header>

      <div className="p-4 space-y-6 flex-1 pb-48">
        <div className="bg-card p-4 rounded-2xl shadow border border-border/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center overflow-hidden border border-border/50 shrink-0">
              {customer.avatarBase64 ? (
                <img
                  src={customer.avatarBase64.startsWith("data:") ? customer.avatarBase64 : `data:image/jpeg;base64,${customer.avatarBase64}`}
                  alt={customer.firstName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-5 h-5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{customer.firstName}</p>
              <p className="text-sm text-muted-foreground truncate">{customer.whatsappFormattedNumber || `+${customer.whatsappNumber}`}</p>
            </div>
            <Link
              href={`/conta?store=${store.slug}`}
              className="shrink-0 inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-border bg-background hover:bg-muted text-sm font-semibold text-foreground transition-colors"
            >
              Ver Conta
            </Link>
          </div>
        </div>

        {(store as any)?.askBirthDate && !customer.birthDate && (
          <div className="bg-card p-4 rounded-2xl shadow border border-border/50 space-y-2">
            <label htmlFor="checkout-birthdate" className="text-sm font-semibold text-foreground">
              Data de nascimento{(store as any)?.birthDateRequired ? "" : " (opcional)"}
            </label>
            <BirthDateInput
              id="checkout-birthdate"
              value={birthDate}
              onChange={setBirthDate}
              className="w-full h-11 rounded-xl border-border bg-background text-base shadow-none"
            />
          </div>
        )}

        {precisaEncomenda && slug && (
          <EncomendaDoPedido
            slug={slug}
            unitId={unitId}
            itens={itensEnc}
            horarios={horariosDosTurnos}
            data={encData}
            hora={encHora}
            turno={encTurno}
            onChange={(d, h, t) => { setEncData(d); setEncHora(h); setEncTurno(t); }}
            destaque={highlightField === "encomenda"}
            cardRef={encomendaCardRef}
          />
        )}

        {!semModalidade && (
        <div ref={deliveryCardRef} className={`bg-card p-5 rounded-2xl shadow border space-y-5 transition-all ${highlightField === "delivery" ? "border-red-400 ring-2 ring-red-300" : "border-border/50"}`}>
          <h2 className="font-bold text-lg flex items-center gap-2"><Truck className="w-5 h-5 text-secondary"/> Como deseja receber?</h2>
          {highlightField === "delivery" && (
            <p className="text-sm text-red-600 font-medium -mt-2">Selecione entrega ou retirada para continuar.</p>
          )}
          <RadioGroup value={deliveryMode} onValueChange={setDeliveryMode} className="grid grid-cols-2 gap-3">
            {store.allowsDelivery && (entregaSemSaida && store.allowsPickup ? (
              <div className="border-2 border-dashed border-border rounded-xl p-4 flex flex-col items-center justify-center gap-1 text-muted-foreground opacity-70 cursor-not-allowed" aria-disabled="true" data-testid="entrega-indisponivel">
                <Truck className="w-6 h-6" />
                <span className="font-semibold text-sm">Entrega</span>
                <span className="text-[11px] leading-tight text-center">Ainda não disponível</span>
              </div>
            ) : (
              <div className={`border-2 rounded-xl p-4 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all ${deliveryMode === 'entrega' ? 'border-accent bg-accent/10 text-foreground' : 'border-border text-muted-foreground hover:bg-muted'}`} onClick={() => setDeliveryMode('entrega')}>
                <Truck className="w-6 h-6" />
                <span className="font-semibold text-sm">Entrega</span>
              </div>
            ))}
            {store.allowsPickup && (
              <div className={`border-2 rounded-xl p-4 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all ${deliveryMode === 'retirada' ? 'border-accent bg-accent/10 text-foreground' : 'border-border text-muted-foreground hover:bg-muted'}`} onClick={() => setDeliveryMode('retirada')}>
                <Store className="w-6 h-6" />
                <span className="font-semibold text-sm">Retirada</span>
              </div>
            )}
          </RadioGroup>

          {store.allowsDelivery && entregaSemSaida && (
            <p className="text-sm rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-3" data-testid="aviso-entrega-nao-configurada">
              {store.allowsPickup
                ? "Esta loja ainda não configurou os bairros de entrega. Por enquanto, só dá para retirar no local."
                : "Esta loja ainda não configurou os bairros de entrega, então não dá para pedir entrega por aqui. Fale com a loja pelo WhatsApp."}
            </p>
          )}

          {deliveryMode === 'retirada' && (() => {
            const s: any = store;
            const line1 = [s.storeStreet, s.storeNumber].filter(Boolean).join(", ");
            const line2 = [s.storeNeighborhood, s.storeCity && s.storeState ? `${s.storeCity} - ${s.storeState}` : (s.storeCity || s.storeState)].filter(Boolean).join(" - ");
            const cep = s.storeZipCode ? String(s.storeZipCode).replace(/^(\d{5})(\d{1,3})$/, "CEP $1-$2") : "";
            const complement = s.storeComplement || "";
            const hasAddress = !!(line1 || line2 || cep);
            if (!hasAddress) return null;
            return (
              <div className="rounded-xl border border-border/60 bg-muted/30 p-3 flex items-start gap-2 animate-in fade-in slide-in-from-top-2 duration-200">
                <MapPin className="w-4 h-4 text-secondary mt-0.5 shrink-0" />
                <div className="text-sm leading-snug">
                  <div className="font-semibold text-foreground">Endereço para retirada</div>
                  {line1 && <div className="text-foreground/90">{line1}{complement ? ` - ${complement}` : ""}</div>}
                  {line2 && <div className="text-muted-foreground">{line2}</div>}
                  {cep && <div className="text-muted-foreground text-xs">{cep}</div>}
                </div>
              </div>
            );
          })()}

          {deliveryMode === 'retirada' && Array.isArray((store as any).pickupMethods) && (store as any).pickupMethods.length > 0 && (
            <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
              <p className="text-sm font-medium">Como será a retirada?</p>
              {[null, ...((store as any).pickupMethods as string[])].map((m) => (
                <button
                  key={m ?? "__loja"}
                  type="button"
                  onClick={() => setPickupMethod(m)}
                  className={`flex w-full min-w-0 items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all text-left ${pickupMethod === m ? 'border-accent bg-accent/10' : 'border-border hover:bg-muted'}`}
                  data-testid={`pickup-method-${m ?? "loja"}`}
                >
                  <span className={`w-4 h-4 rounded-full border-2 shrink-0 ${pickupMethod === m ? 'border-accent bg-accent' : 'border-muted-foreground/40'}`} />
                  <span className="font-semibold text-sm">{m ?? "Na loja"}</span>
                </button>
              ))}
              {pickupMethod && (
                <p className="text-sm text-muted-foreground" data-testid="pickup-method-hint">
                  Você deverá mandar o <strong className="text-foreground">{pickupMethod}</strong> quando o pedido estiver pronto.
                </p>
              )}
            </div>
          )}

          {deliveryMode === 'entrega' && !entregaSemSaida && (
            <div
              ref={addressBlockRef}
              className={`space-y-4 animate-in fade-in slide-in-from-top-2 pt-3 border-t border-border/50 transition-all ${
                highlightField === "address" || highlightField === "addressForm" || highlightField === "noDelivery" || highlightField === "feeLoading"
                  ? "rounded-xl ring-2 ring-red-300 border border-red-300 p-3 -mx-1"
                  : ""
              }`}
            >
              <h3 className="font-bold text-sm text-muted-foreground">Endereço de Entrega</h3>
              {highlightField === "address" && (
                <p className="text-sm text-red-600 font-medium">{addressMissingMsg}</p>
              )}
              {highlightField === "addressForm" && (
                <p className="text-sm text-red-600 font-medium">Salve o novo endereço (ou cancele) antes de continuar.</p>
              )}
              {highlightField === "noDelivery" && (
                <p className="text-sm text-red-600 font-medium">Não entregamos nesse endereço. Escolha retirada ou outro endereço.</p>
              )}
              {highlightField === "feeLoading" && (
                <p className="text-sm text-red-600 font-medium">{feeNotResolvedMsg}</p>
              )}

              {savedAddresses.length > 0 && (
                <div className="space-y-2">
                  <RadioGroup value={selectedAddressId} onValueChange={handleSelectSavedAddress} className="space-y-2">
                    {savedAddresses.map(a => (
                      <label
                        key={a.id}
                        htmlFor={`addr-${a.id}`}
                        className={`flex w-full min-w-0 items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${selectedAddressId === a.id && addressFormMode === "hidden" ? 'border-accent bg-accent/10' : 'border-border hover:bg-muted'}`}
                      >
                        <RadioGroupItem value={a.id} id={`addr-${a.id}`} className="mt-1" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm truncate">
                              {a.label || `${a.street}, ${a.number}`}
                            </span>
                            {a.isDefault && (
                              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-bold">Padrão</span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {a.street}, {a.number}{a.complement ? ` - ${a.complement}` : ''} • {a.neighborhood}{a.city ? `, ${a.city}` : ''}
                          </p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); handleEditSaved(a.id); }}
                            className="p-1.5 rounded-lg hover:bg-background/80 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label="Editar endereço"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); handleDeleteSaved(a.id); }}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors"
                            aria-label="Remover endereço"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </label>
                    ))}
                  </RadioGroup>
                  {addressFormMode === "hidden" && (
                    <button
                      type="button"
                      onClick={() => handleSelectSavedAddress("__new__")}
                      className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-border text-muted-foreground hover:text-foreground hover:bg-muted text-sm font-semibold transition-colors"
                    >
                      <Plus className="w-4 h-4" /> Adicionar novo endereço
                    </button>
                  )}
                </div>
              )}

              {addressFormMode !== "hidden" && (
                <div className="space-y-3">
                  {savedAddresses.length > 0 && (
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-foreground">
                        {addressFormMode === "edit" ? "Editar endereço" : "Novo endereço"}
                      </p>
                      <button
                        type="button"
                        onClick={handleCancelAddressForm}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                      >
                        <X className="w-3.5 h-3.5" /> Cancelar
                      </button>
                    </div>
                  )}

              {!manualMode && feeType === "neighborhood" ? (
                <div className="space-y-3">
                  <div className="space-y-1">
                    {/* O select do bairro fica SEMPRE, mesmo com o campo
                        desligado na config: aqui ele é o seletor da TAXA, não
                        um campo de endereço — sem ele o frete não existe. */}
                    {/* Lista longa ganha a pesquisa (Pedro, 30/10/2026); curta segue no select do sistema. */}
                    <SeletorDeBairro
                      id="addr-f-neighborhood"
                      value={selectedNeighborhood}
                      bairros={bairrosEmOrdem}
                      aceitaOutro={neighborhoodMeta.acceptUnlisted}
                      placeholder="Selecione o bairro"
                      onChange={v => { setSelectedNeighborhood(v); setAddress(a => ({ ...a, neighborhood: v === "__other__" ? "" : v })); }}
                      className={`flex h-10 w-full rounded-md border px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${flagMissing.includes("neighborhood") && !address.neighborhood.trim() ? "bg-amber-50 border-amber-400 ring-2 ring-amber-200" : "border-input bg-muted/30"}`}
                    />
                    {selectedNeighborhood === "__other__" && (
                      <Input placeholder="Digite o bairro" value={address.neighborhood} onChange={e => setAddress({ ...address, neighborhood: e.target.value })} className={`mt-2 ${flagMissing.includes("neighborhood") && !address.neighborhood.trim() ? "bg-amber-50 border-amber-400 ring-2 ring-amber-200" : "bg-muted/30"}`} autoFocus />
                    )}
                  </div>
                  {/* Mesma experiência progressiva da entrega por distância
                      (GPS → busca recolhida → "busca não está funcionando" →
                      formulário manual), com duas diferenças: o bairro vem do
                      select acima e é intocável pela busca
                      (`neighborhoodFromList`), e não há mapa (a taxa não
                      depende do pin). O que impede o pedido sem endereço não é
                      este layout, é o gate nbBaseOk no addressValid — foi a
                      ausência dele que deixou 17 pedidos saírem sem rua em
                      08/08/2026. */}
                  <AddressAutocomplete
                    googleMapsApiKey={googleMapsKey || ""}
                    neighborhoodFromList
                    missingKeys={flagMissing}
                    value={{
                      street: address.street,
                      number: address.number,
                      complement: address.complement,
                      neighborhood: address.neighborhood,
                      city: address.city,
                      state: address.state,
                      zipCode: address.zipCode,
                      reference: address.reference,
                      lat: address.lat,
                      lng: address.lng,
                      formattedAddress: "",
                    }}
                    fields={(store as any)?.manualAddressFields ? manualFields : undefined}
                    onChange={(addr) => {
                    if (addr.origin === "google") googleZipRef.current = (addr.zipCode || "").replace(/\D/g, "");
                      setAddress(prev => ({
                        ...prev,
                        street: addr.street,
                        number: addr.number,
                        complement: addr.complement,
                        // O bairro é do select da loja; o componente já o
                        // preserva (ponto único de escrita), o prev aqui só
                        // deixa a regra explícita também deste lado.
                        neighborhood: prev.neighborhood,
                        city: addr.city,
                        state: addr.state,
                        zipCode: addr.zipCode,
                        reference: addr.reference ?? prev.reference,
                        lat: addr.lat,
                        lng: addr.lng,
                      }));
                    }}
                    showMap={false}
                    mapHeight={200}
                    placeholder="Buscar endereço de entrega..."
                    compact={true}
                    manualMode={userManualEntry}
                    onManualModeChange={setUserManualEntry}
                    hideManualToggle={true}
                    enableGeolocation={true}
                    openForEditing={addressFormMode === "edit"}
                    onConfirmLocation={() => {
                      const missing = nbDrawnKeys
                        .filter(k => manualFields[k].enabled && manualFields[k].required && !fieldFilled(k))
                        .map(k => MANUAL_ADDRESS_FIELD_LABELS[k]);
                      if (missing.length > 0) {
                        toast({ title: `Preencha: ${missing.join(", ")}`, variant: "destructive" });
                        return false;
                      }
                      if (savedAddresses.length > 0) return true;
                      // Só rua e nº aqui: o bairro tem gate próprio (o select
                      // e o feeNotResolved) e pode ser escolhido depois.
                      const faltaRuaNum = (["street", "number"] as ManualAddressFieldKey[])
                        .some(k => drawnBase(k) && !fieldFilled(k));
                      if (faltaRuaNum) {
                        toast({ title: "Preencha rua e número antes de confirmar", variant: "destructive" });
                        return false;
                      }
                      void handleSaveAddressForm("Casa");
                      return true;
                    }}
                  />
                </div>
              ) : manualMode ? (
                <div className="grid grid-cols-2 gap-3">
                  {MANUAL_ADDRESS_FIELD_ORDER.filter(k => manualFields[k].enabled).map(key => {
                    const cfg = manualFields[key];
                    const addrKey = MANUAL_ADDRESS_ADDRESS_KEY[key] as keyof typeof address;
                    const colSpan = key === "complement" || key === "reference" || key === "street" ? "col-span-2" : (key === "number" ? "col-span-1" : "col-span-2");
                    if (key === "cep") {
                      return (
                        <div key={key} className="col-span-2 space-y-1">
                          <Input
                            id="addr-f-cep"
                            placeholder={MANUAL_ADDRESS_FIELD_PLACEHOLDERS[key] + (cfg.required ? " *" : "")}
                            value={address.zipCode.replace(/^(\d{5})(\d{1,3})$/, "$1-$2")}
                            onChange={e => { const digits = e.target.value.replace(/\D/g, "").slice(0, 8); setAddress({ ...address, zipCode: digits }); }}
                            className={missCls("cep")}
                            maxLength={10}
                          />
                        </div>
                      );
                    }
                    if (key === "state") {
                      return (
                        <div key={key} className="col-span-2 sm:col-span-1 space-y-1">
                          <Input
                            id="addr-f-state"
                            placeholder={MANUAL_ADDRESS_FIELD_PLACEHOLDERS[key] + (cfg.required ? " *" : "")}
                            value={address.state}
                            onChange={e => setAddress({ ...address, state: e.target.value.toUpperCase().slice(0, 2) })}
                            className={`${missCls("state")} uppercase`}
                            maxLength={2}
                          />
                        </div>
                      );
                    }
                    if (key === "number") {
                      return (
                        <div key={key} className="col-span-1 space-y-1">
                          <Input
                            id="addr-f-number"
                            placeholder={MANUAL_ADDRESS_FIELD_PLACEHOLDERS[key] + (cfg.required ? " *" : "")}
                            value={address.number}
                            onChange={e => setAddress({ ...address, number: e.target.value })}
                            className={missCls("number")}
                            maxLength={20}
                          />
                        </div>
                      );
                    }
                    // Entrega por bairro: mesmo no endereço manual o bairro vem
                    // dos pré-cadastrados (o texto livre não casa com a tabela
                    // de taxas e a entrega não resolve).
                    if (key === "neighborhood" && feeType === "neighborhood") {
                      return (
                        <div key={key} className="col-span-2 space-y-1">
                          <SeletorDeBairro
                            id="addr-f-neighborhood"
                            value={selectedNeighborhood}
                            bairros={bairrosEmOrdem}
                            aceitaOutro={neighborhoodMeta.acceptUnlisted}
                            placeholder={`Selecione o bairro${cfg.required ? " *" : ""}`}
                            onChange={v => { setSelectedNeighborhood(v); setAddress(a => ({ ...a, neighborhood: v === "__other__" ? "" : v })); }}
                            className={`flex h-10 w-full rounded-md border px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${flagMissing.includes("neighborhood") && !fieldFilled("neighborhood") ? "bg-amber-50 border-amber-400 ring-2 ring-amber-200" : "border-input bg-muted/30"}`}
                            testId="checkout-manual-neighborhood-select"
                          />
                          {selectedNeighborhood === "__other__" && (
                            <Input placeholder="Digite o bairro" value={address.neighborhood} onChange={e => setAddress({ ...address, neighborhood: e.target.value })} className="bg-muted/30 mt-2" autoFocus />
                          )}
                        </div>
                      );
                    }
                    return (
                      <div key={key} className={`${colSpan} space-y-1`}>
                        <Input
                          id={`addr-f-${key}`}
                          placeholder={MANUAL_ADDRESS_FIELD_PLACEHOLDERS[key] + (cfg.required ? " *" : "")}
                          value={String((address as any)[addrKey] || "")}
                          onChange={e => setAddress({ ...address, [addrKey]: e.target.value })}
                          className={missCls(key)}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <AddressAutocomplete
                  googleMapsApiKey={googleMapsKey || ""}
                  missingKeys={flagMissing}
                  value={{
                    street: address.street,
                    number: address.number,
                    complement: address.complement,
                    neighborhood: address.neighborhood,
                    city: address.city,
                    // Era `""` fixo: o Google preenchia a UF, o onChange abaixo
                    // não devolvia, e o campo voltava vazio a cada render — de
                    // quebra parecia que não dava pra digitar, porque a tecla
                    // sumia no próximo ciclo.
                    state: address.state,
                    zipCode: address.zipCode,
                    reference: address.reference,
                    lat: address.lat,
                    lng: address.lng,
                    formattedAddress: "",
                  }}
                  // Só manda a configuração pra loja que REALMENTE tem uma
                  // salva: os defaults trazem CEP/cidade/estado desligados
                  // (foram desenhados pro formulário manual enxuto) e aplicá-los
                  // a quem nunca abriu a seção sumiria com campos — junto com o
                  // preenchimento automático por CEP.
                  fields={(store as any)?.manualAddressFields ? camposDaBusca : undefined}
                  onChange={(addr) => {
                    if (addr.origin === "google") googleZipRef.current = (addr.zipCode || "").replace(/\D/g, "");
                    setAddress(prev => ({
                      ...prev,
                      street: addr.street,
                      number: addr.number,
                      complement: addr.complement,
                      neighborhood: addr.neighborhood,
                      city: addr.city,
                      state: addr.state,
                      zipCode: addr.zipCode,
                      // Endereço novo vindo do Google não traz referência — o que
                      // o cliente já digitou fica.
                      reference: addr.reference ?? prev.reference,
                      lat: addr.lat,
                      lng: addr.lng,
                    }));
                    if (feeType === "neighborhood" && addr.neighborhood) {
                      setSelectedNeighborhood(addr.neighborhood);
                    }
                  }}
                  showMap={feeType === "distance"}
                  mapHeight={200}
                  placeholder="Buscar endereço de entrega..."
                  compact={true}
                  manualMode={userManualEntry}
                  onManualModeChange={setUserManualEntry}
                  hideManualToggle={true}
                  enableGeolocation={true}
                  rewriteOnConfirm={true}
                  openForEditing={addressFormMode === "edit"}
                  onConfirmLocation={() => {
                    // 1º endereço do cliente: sem div de "salvar como" — o
                    // Confirmar localização já salva automaticamente como
                    // "Casa". Rua/número/bairro são exigidos ANTES (retornar
                    // false veta a confirmação no componente).
                    // Obrigatórios da loja primeiro: vetar aqui mantém o painel
                    // aberto com o campo à vista (confirmar recolhe pro box
                    // verde e o cliente não acharia o que falta).
                    const missing = missingRequiredLabels(autocompleteVisibleKeys);
                    if (missing.length > 0) {
                      toast({ title: `Preencha: ${missing.join(", ")}`, variant: "destructive" });
                      return false;
                    }
                    if (savedAddresses.length > 0) return true;
                    if (!baseAddrOk) {
                      toast({ title: "Preencha rua, número e bairro antes de confirmar", variant: "destructive" });
                      return false;
                    }
                    void handleSaveAddressForm("Casa");
                    return true;
                  }}
                />
              )}

                  {/* 1º endereço no fluxo com mapa: sem div de salvar — o
                      "Confirmar localização" auto-salva como "Casa". A div só
                      aparece pra quem já tem endereços (rótulos: Trabalho...)
                      ou está no modo manual (que não tem o botão de confirmar). */}
                  {(savedAddresses.length > 0 || manualMode || userManualEntry) && (
                    <>
                      <div className="pt-3 mt-3 border-t border-border/60 space-y-1.5">
                        <Label className="text-sm">Salvar esse endereço como:</Label>
                        <Input
                          placeholder="Ex: Casa, Trabalho"
                          value={addressLabel}
                          onChange={e => setAddressLabel(e.target.value)}
                          className="bg-muted/30"
                          maxLength={40}
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={() => handleSaveAddressForm()}
                        disabled={savingAddress || (manualMode ? !manualRequiredOk : !baseAddrOk)}
                        className="w-full"
                      >
                        {savingAddress ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <><Check className="w-4 h-4 mr-1" /> {addressFormMode === "edit" ? "Salvar alterações" : "Salvar endereço"}</>
                        )}
                      </Button>
                    </>
                  )}
                  {/* Escape pra entrada 100% manual mora DENTRO da área de
                      busca do AddressAutocomplete ("busca não está funcionando",
                      link discreto) — o botão externo daqui foi removido. */}
                </div>
              )}

              {feeLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" /> Calculando taxa de entrega...
                </div>
              )}

              {feePending && (
                <div className="p-3 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 text-sm text-amber-900 dark:text-amber-200 flex items-start gap-2">
                  <Truck className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>O frete será calculado e informado após o envio do pedido.</span>
                </div>
              )}

              {!feeLoading && feeType === "distance" && dynamicFee && (
                <div className={`p-3 rounded-xl border ${dynamicFee.noDelivery ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}>
                  {dynamicFee.noDelivery ? (
                    <div className="flex items-center gap-2 text-red-700 text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      <span>Não entregamos nessa distância ({dynamicFee.distanceText || `${dynamicFee.distanceKm} km`}). Você pode optar por retirada no local.</span>
                    </div>
                  ) : (
                    <div className="text-sm text-green-800">
                      <span className="font-medium">Distância: {dynamicFee.distanceText || `${dynamicFee.distanceKm} km`}{dynamicFee.durationText ? ` — ${dynamicFee.durationText}` : ''}</span>
                      {dynamicFee.feePending ? (
                        // Acima da última faixa numa loja que combina o frete: fee null é "a calcular", não R$ 0,00.
                        <span className="ml-2">— Frete: <span className="font-bold">a calcular após o pedido</span></span>
                      ) : (deliveryFreeApplied || cupomFreteZeraTaxa) ? (
                        <span className="ml-2">— <span className="line-through text-muted-foreground">{formatCurrency(dynamicFee.fee!)}</span> <span className="font-bold text-green-700">Entrega grátis!</span></span>
                      ) : (
                        <span className="ml-2">— Taxa: <span className="font-bold">{formatCurrency(dynamicFee.fee!)}</span></span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!feeLoading && feeType === "neighborhood" && dynamicFee && selectedNeighborhood && (selectedNeighborhood !== "__other__" || address.neighborhood.trim()) && (dynamicFee.noDelivery || !taxaOculta) && (
                <div className={`p-3 rounded-xl border ${dynamicFee.noDelivery ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}>
                  {dynamicFee.noDelivery ? (
                    <div className="flex items-center gap-2 text-red-700 text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      <span>Não entregamos nesse bairro. Você pode optar por retirada no local.</span>
                    </div>
                  ) : (
                    <div className="text-sm text-green-800">
                      <span className="font-medium">Bairro: {selectedNeighborhood === "__other__" ? address.neighborhood : dynamicFee.neighborhood}</span>
                      {dynamicFee.feePending ? (
                        // Frete pendente: fee é null e formatCurrency(null!)
                        // imprimia "R$ 0,00" — desmentindo o aviso âmbar de
                        // "calculado após o pedido" logo acima.
                        <span className="ml-2">— Frete: <span className="font-bold">a calcular após o pedido</span></span>
                      ) : (deliveryFreeApplied || cupomFreteZeraTaxa) ? (
                        <span className="ml-2">— <span className="line-through text-muted-foreground">{formatCurrency(dynamicFee.fee!)}</span> <span className="font-bold text-green-700">Entrega grátis!</span></span>
                      ) : (
                        <span className="ml-2">— Taxa: <span className="font-bold">{formatCurrency(dynamicFee.fee ?? 0)}</span></span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!feeLoading && !taxaOculta && faltaParaMinimoDoBairro > 0 && bairroDaTaxa && (
                <div ref={minimoDoBairroRef} data-testid="checkout-minimo-bairro" className="p-3 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 text-sm text-amber-900 dark:text-amber-200 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>Para entregar em {bairroDaTaxa.name}, o pedido mínimo é <strong>{formatCurrency(minimoDoBairro)}</strong>. Faltam <strong>{formatCurrency(faltaParaMinimoDoBairro)}</strong>.</span>
                  </div>
                  <Link
                    href={`/loja/${slug}${unitId ? `?unitId=${encodeURIComponent(unitId)}` : ""}`}
                    className="inline-flex items-center gap-1 font-semibold underline underline-offset-2"
                  >
                    <Plus className="w-4 h-4" /> Adicionar mais itens
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {effectivePayments.length > 0 && (
          <div ref={paymentCardRef} className={`bg-card p-5 rounded-2xl shadow border space-y-4 transition-all ${highlightField === "payment" ? "border-red-400 ring-2 ring-red-300" : "border-border/50"}`}>
            <h2 className="font-bold text-lg flex items-center gap-2"><CreditCard className="w-5 h-5 text-secondary"/> Forma de Pagamento</h2>
            {highlightField === "payment" && (
              <p className="text-sm text-red-600 font-medium -mt-2">Escolha uma forma de pagamento para continuar.</p>
            )}
            <div className="grid gap-2">
              {effectivePayments.map((p: string) => {
                const cfg = (store as any)?.cardSurchargeConfig?.[p];
                const isSurchargeMethod = p === "Débito" || p === "Crédito" || p === "Vale Refeição" || p === "Vale Alimentação";
                const showSurcharge = isSurchargeMethod && paymentMethod === p && cfg && cfg.enabled && Number(cfg.value) > 0;
                const surchargeLabel = showSurcharge
                  ? cfg.type === "percent"
                    ? `Este pagamento tem acréscimo de ${Number(cfg.value).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`
                    : `Este pagamento tem acréscimo de ${formatCurrency(Number(cfg.value))}`
                  : "";
                return (<div key={p}>
                  <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentMethod === p ? 'border-accent bg-accent/10' : 'border-border hover:bg-muted'}`}>
                    <input type="radio" name="payment" value={p} checked={!paymentMixed && paymentMethod === p} onChange={() => { setPaymentMixed(false); setPaymentMethod(p); setNeedsChange(p === 'Dinheiro' ? false : null); setChangeFor(""); }} className="w-4 h-4 text-primary accent-primary" />
                    <span className="font-semibold text-foreground">{p}</span>
                  </label>
                  {showSurcharge && (
                    <p className="text-xs text-muted-foreground mt-1 px-1">{surchargeLabel}</p>
                  )}
                  {p === 'Dinheiro' && paymentMethod === 'Dinheiro' && (
                    <div className="mt-2 px-1 space-y-3">
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setNeedsChange(false)}
                          className={`flex-1 py-2.5 rounded-xl border-2 font-semibold text-sm transition-all ${needsChange === false ? 'border-accent bg-accent/10' : 'border-border hover:bg-muted'}`}
                        >
                          Não preciso de troco
                        </button>
                        <button
                          type="button"
                          onClick={() => setNeedsChange(true)}
                          className={`flex-1 py-2.5 rounded-xl border-2 font-semibold text-sm transition-all ${needsChange === true ? 'border-accent bg-accent/10' : 'border-border hover:bg-muted'}`}
                        >
                          Preciso de troco
                        </button>
                      </div>
                      {needsChange === true && (() => {
                        const digits = changeFor.replace(/\D/g, "");
                        const cents = digits ? parseInt(digits, 10) : 0;
                        const changeForValue = cents / 100;
                        const formattedInput = digits ? formatCurrency(changeForValue) : "";
                        const changeAmount = changeForValue - totalTela;
                        const insufficient = digits.length > 0 && changeAmount < 0;
                        return (
                          <div className="space-y-1.5">
                            <Label className="text-sm">Troco para quanto?</Label>
                            <Input
                              ref={changeForInputRef}
                              inputMode="numeric"
                              placeholder="Ex: R$ 50,00"
                              value={formattedInput}
                              onChange={e => setChangeFor(e.target.value.replace(/\D/g, ""))}
                              className={`h-11 transition-all ${highlightField === "changeFor" ? "border-red-400 ring-2 ring-red-300" : ""}`}
                            />
                            {highlightField === "changeFor" && !insufficient && digits.length === 0 && (
                              <p className="text-sm text-red-600 font-medium">Informe o valor do troco para continuar.</p>
                            )}
                            {digits.length > 0 && !insufficient && (
                              <p className="text-sm text-green-700 font-semibold">
                                Troco a devolver: {formatCurrency(changeAmount)}
                              </p>
                            )}
                            {insufficient && (
                              <p className="text-sm text-red-600 font-medium">
                                O valor deve ser maior ou igual ao total ({formatCurrency(totalTela)}).
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {crediarioPublicNames.includes(p) && paymentMethod === p && !paymentMixed && (() => {
                    const cfg = crediarioCfgs.find((m) => m.name === p) ?? null;
                    const max = Math.min(Math.max(cfg?.installments ?? 1, 1), 24);
                    const chosen = Math.min(crediarioInstallments, max);
                    const jurosChosen = crediarioInterestFor(cfg, chosen);
                    const totalChosen = totalTela * (1 + jurosChosen / 100);
                    return (
                      <div className="mt-2 px-1 space-y-1.5">
                        <Label className="text-sm">Em quantas parcelas?</Label>
                        <select
                          value={chosen}
                          onChange={(e) => setCrediarioInstallments(parseInt(e.target.value, 10) || 1)}
                          className="w-full h-11 border-2 border-border rounded-xl px-3 text-sm bg-background font-semibold"
                          data-testid="checkout-crediario-installments"
                        >
                          {Array.from({ length: max }, (_, i) => i + 1).map((n) => {
                            const juros = crediarioInterestFor(cfg, n);
                            const totalN = totalTela * (1 + juros / 100);
                            const label = n === 1
                              ? `1x de ${formatCurrency(totalN)}${juros > 0 ? ` (total ${formatCurrency(totalN)})` : " sem juros"}`
                              : `${n}x de ${formatCurrency(totalN / n)}${juros > 0 ? ` (total ${formatCurrency(totalN)})` : " sem juros"}`;
                            return <option key={n} value={n}>{label}</option>;
                          })}
                        </select>
                        <p className="text-xs text-muted-foreground">
                          {jurosChosen > 0 ? `Total a prazo ${formatCurrency(totalChosen)} (juros incluídos). ` : ""}
                          A loja combina as datas de vencimento com você.
                        </p>
                      </div>
                    );
                  })()}
                </div>
                );
              })}

              {(store as { allowMixedPayments?: boolean } | undefined)?.allowMixedPayments === true && effectivePayments.length >= 2 && (
                <div>
                  <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentMixed ? 'border-accent bg-accent/10' : 'border-border hover:bg-muted'}`}>
                    <input
                      type="radio"
                      name="payment"
                      checked={paymentMixed}
                      onChange={() => {
                        setPaymentMixed(true);
                        setPaymentMethod("");
                        setNeedsChange(null);
                        setChangeFor("");
                        setMixedMethod1(effectivePayments[0] || "");
                        setMixedMethod2(effectivePayments[1] || "");
                        setMixedAmount1(0);
                      }}
                      className="w-4 h-4 text-primary accent-primary"
                    />
                    <span className="font-semibold text-foreground">Pagar com duas formas</span>
                  </label>
                  {paymentMixed && mixed && (
                    <div className="mt-2 px-1 space-y-2">
                      <div className="flex gap-2 items-center">
                        <select
                          className="flex-1 min-w-0 h-11 border border-border rounded-xl px-2 text-sm bg-background"
                          value={mixed.m1}
                          onChange={(e) => {
                            setMixedMethod1(e.target.value);
                            if (e.target.value === mixed.m2) {
                              setMixedMethod2(effectivePayments.find((p: string) => p !== e.target.value) || "");
                            }
                          }}
                        >
                          {effectivePayments.map((p: string) => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <CurrencyInput
                          value={mixed.a1 > 0 ? mixed.a1 : ""}
                          onChange={(v) => setMixedAmount1(parseFloat(v || "0") || 0)}
                          className="w-32 h-11 shrink-0 text-right"
                          placeholder="R$ 0,00"
                        />
                      </div>
                      <div className="flex gap-2 items-center">
                        <select
                          className="flex-1 min-w-0 h-11 border border-border rounded-xl px-2 text-sm bg-background"
                          value={mixed.m2}
                          onChange={(e) => setMixedMethod2(e.target.value)}
                        >
                          {effectivePayments.filter((p: string) => p !== mixed.m1).map((p: string) => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <div className="w-32 h-11 shrink-0 flex items-center justify-end px-3 rounded-xl border border-border bg-muted/40 text-sm font-semibold">
                          {formatCurrency(mixedTela?.a2 ?? mixed.a2)}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">Digite quanto vai pagar na primeira forma — o restante fica na segunda.</p>
                      {(mixedTela?.surcharge ?? mixed.surcharge) > 0 && (
                        <p className="text-xs text-muted-foreground">Inclui acréscimo de {formatCurrency(mixedTela?.surcharge ?? mixed.surcharge)} da forma com taxa.</p>
                      )}
                      {mixedCashAmount > 0 && (
                        <div className="pt-1 space-y-3">
                          <div className="flex gap-3">
                            <button
                              type="button"
                              onClick={() => setNeedsChange(false)}
                              className={`flex-1 py-2.5 rounded-xl border-2 font-semibold text-sm transition-all ${needsChange === false ? 'border-accent bg-accent/10' : 'border-border hover:bg-muted'}`}
                            >
                              Não preciso de troco
                            </button>
                            <button
                              type="button"
                              onClick={() => setNeedsChange(true)}
                              className={`flex-1 py-2.5 rounded-xl border-2 font-semibold text-sm transition-all ${needsChange === true ? 'border-accent bg-accent/10' : 'border-border hover:bg-muted'}`}
                            >
                              Preciso de troco
                            </button>
                          </div>
                          {needsChange === true && (() => {
                            const digits = changeFor.replace(/\D/g, "");
                            const cents = digits ? parseInt(digits, 10) : 0;
                            const changeForValue = cents / 100;
                            const formattedInput = digits ? formatCurrency(changeForValue) : "";
                            const changeAmount = changeForValue - mixedCashTela;
                            const insufficient = digits.length > 0 && changeAmount < 0;
                            return (
                              <div className="space-y-1.5">
                                <Label className="text-sm">Troco para quanto? (parte em dinheiro: {formatCurrency(mixedCashTela)})</Label>
                                <Input
                                  inputMode="numeric"
                                  placeholder="Ex: R$ 50,00"
                                  value={formattedInput}
                                  onChange={e => setChangeFor(e.target.value.replace(/\D/g, ""))}
                                  className="h-11"
                                />
                                {digits.length > 0 && !insufficient && (
                                  <p className="text-sm text-green-700 font-semibold">Troco a devolver: {formatCurrency(changeAmount)}</p>
                                )}
                                {insufficient && (
                                  <p className="text-sm text-red-600 font-medium">O valor deve ser maior ou igual à parte em dinheiro ({formatCurrency(mixedCashTela)}).</p>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {loyaltyOn && programa && (
          <PontosDoPedido
            programa={programa}
            verificado={loyalty?.verified === true}
            saldo={saldoPontos}
            escolhidos={premiosEscolhidos}
            onEscolher={setPremiosEscolhidos}
            problemaDe={(r) => problemaDoPremio(r)}
            rendimento={rendimentoPontos}
            descontoDe={(id) => rewardMath.perUse[rewardUses.findIndex((u) => u.rewardId === id)] ?? 0}
            pontosUsadosDe={(id) => rewardUses.find((u) => u.rewardId === id)?.points ?? 0}
            onConfirmar={() => setConfirmarNumero(true)}
          />
        )}
        {loyaltyOn && (
          <ConfirmNumberDialog
            open={confirmarNumero}
            onOpenChange={setConfirmarNumero}
            storeSlug={slug}
            onVerified={() => void refetchLoyalty()}
          />
        )}

        {/* Só aparece se a loja tem cupom ativo (hasCoupons do catálogo). */}
        {!!(store as any)?.hasCoupons && (
        <div className="bg-card p-4 rounded-2xl shadow border border-border/50 space-y-2">
          <label htmlFor="checkout-coupon" className="text-sm font-semibold text-foreground flex items-center gap-2">
            <TicketPercent className="w-4 h-4 text-primary" /> Cupom de desconto
          </label>
          {appliedCoupons.map((cup) => (
            <div key={cup.code} className="flex items-center justify-between gap-2 rounded-xl border border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800 px-3 py-2.5">
              <p className="text-sm font-semibold text-green-700 dark:text-green-400 min-w-0">
                {cup.discountType === "free_delivery"
                  ? `${cup.code} aplicado — a entrega sai grátis${deliveryFee > 0 && !taxaOculta ? ` (você economiza ${formatCurrency(deliveryFee)})` : ""}`
                  : `${cup.code} aplicado — você economiza ${formatCurrency(taxaOculta && cuponsSemFrete.length === 1 ? couponLineTela : economiaDoCupom(cup))}`}
              </p>
              <button
                type="button"
                onClick={() => removeCoupon(cup.code)}
                className="text-muted-foreground hover:text-destructive p-1 shrink-0"
                aria-label={`Remover cupom ${cup.code}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          {(appliedCoupons.length === 0 || stackable) && (
            <>
              <div className="flex gap-2">
                <Input
                  id="checkout-coupon"
                  value={couponInput}
                  onChange={(e) => {
                    setCouponInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 30));
                    if (couponError) setCouponError(null);
                  }}
                  placeholder={appliedCoupons.length > 0 ? "Adicionar outro cupom" : "Tem um cupom? Digite aqui"}
                  className="flex-1 min-w-0 h-11 font-bold tracking-wider uppercase"
                  autoComplete="off"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 shrink-0"
                  onClick={() => applyCoupon()}
                  disabled={couponChecking || !couponInput.trim()}
                >
                  {couponChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aplicar"}
                </Button>
              </div>
              {couponError && <p className="text-sm text-destructive">{couponError}</p>}
            </>
          )}
        </div>
        )}

        {pedePresente && (
          <div
            ref={giftCardRef}
            className={`bg-card p-4 rounded-2xl shadow border space-y-3 transition-all ${highlightField === "gift" ? "border-red-400 ring-2 ring-red-300" : "border-border/50"}`}
            data-testid="checkout-presente"
          >
            <p className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Gift className="w-4 h-4 text-primary" /> É para presente?
              {!presenteObrigatorio && <span className="font-normal text-muted-foreground">(opcional)</span>}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setEhPresente(false)}
                className={`flex-1 py-2.5 rounded-xl border-2 font-semibold text-sm transition-all ${ehPresente === false ? 'border-accent bg-accent/10' : 'border-border hover:bg-muted'}`}
                data-testid="checkout-presente-nao"
              >
                Não
              </button>
              <button
                type="button"
                onClick={() => setEhPresente(true)}
                className={`flex-1 py-2.5 rounded-xl border-2 font-semibold text-sm transition-all ${ehPresente === true ? 'border-accent bg-accent/10' : 'border-border hover:bg-muted'}`}
                data-testid="checkout-presente-sim"
              >
                Sim
              </button>
            </div>
            {highlightField === "gift" && (
              <p className="text-sm text-red-600 font-medium">Escolha Não ou Sim para continuar.</p>
            )}
            {ehPresente === true && (
              <div className="space-y-1.5">
                <Label htmlFor="checkout-presente-nome" className="text-sm">Quem vai receber o presente?</Label>
                <Input
                  id="checkout-presente-nome"
                  ref={giftNameInputRef}
                  value={quemRecebe}
                  onChange={(e) => setQuemRecebe(e.target.value)}
                  maxLength={80}
                  placeholder="Nome de quem vai receber"
                  className={`h-11 transition-all ${highlightField === "giftName" ? "border-red-400 ring-2 ring-red-300" : ""}`}
                  data-testid="checkout-presente-nome"
                />
                {highlightField === "giftName" && !quemRecebe.trim() && (
                  <p className="text-sm text-red-600 font-medium">Informe quem vai receber o presente para continuar.</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Observação do pedido inteiro (09/10/2026, Pedro: "Como assim não há um campo de observação geral do
            pedido?"). O `notes` sempre foi enviado, mas sem campo na tela ia vazio. A de cada item continua no produto. */}
        <div className="bg-card p-4 rounded-2xl shadow border border-border/50 space-y-2">
          <label htmlFor="checkout-observacoes" className="text-sm font-semibold text-foreground flex items-center gap-2">
            <MessageSquareText className="w-4 h-4 text-primary" /> Observações do pedido
            <span className="font-normal text-muted-foreground">(opcional)</span>
          </label>
          <textarea
            id="checkout-observacoes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="Alguma observação para a loja?"
            className="w-full rounded-xl border border-input bg-card px-3 py-2 text-base sm:text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
            data-testid="checkout-observacoes"
          />
        </div>

        {lojaDesativada ? (
          <StoreInactiveCard />
        ) : !catalog?.isOpen ? (
          <ClosedStoreCard businessHours={store?.businessHours} type="pedido" blocked={closedAndBlocked} />
        ) : null}

      </div>

      <div className="fixed bottom-0 inset-x-0 bg-card border-t border-border/50 p-4 max-w-3xl mx-auto shadow-[0_-10px_40px_rgba(0,0,0,0.05)] z-20">
        <div className="flex justify-between items-end mb-4">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Subtotal: {formatCurrency(grossSubtotal)}</p>
            {qtyPromoEntries.map((e) => (
              <p key={e.productId} className="text-sm font-medium text-green-600">
                Promoção {e.productName}: -{formatCurrency(e.discount)}
              </p>
            ))}
            {cupomDaLinha && couponLineTela > 0 && (
              <p className="text-sm font-medium text-green-600">
                Cupom {cupomDaLinha.code}: -{formatCurrency(couponLineTela)}
              </p>
            )}
            {premiosComDesconto.map(({ u, d }) => (
              <p key={u.rewardId} className="text-sm font-medium text-green-600" data-testid="checkout-pontos-linha">
                Pontos ({u.name}): -{formatCurrency(d)}
              </p>
            ))}
            {deliveryMode === 'entrega' && (
              <p className="text-sm text-muted-foreground">
                {entregaSemSaida ? (
                  <>Entrega: <span className="font-medium text-foreground">não configurada pela loja</span></>
                ) : feePending ? (
                  <>Frete: <span className="font-medium text-foreground">calculado após pedido</span></>
                ) : taxaOculta ? (
                  <>Entrega: <span className="font-medium text-foreground">informe o endereço</span></>
                ) : (
                  <>Entrega: {noDeliveryBlocked ? (
                    <span className="text-red-500 font-medium">Indisponível</span>
                  ) : cupomFreteZeraTaxa || premioFreteZeraTaxa ? (
                    <><span className="line-through">{formatCurrency(deliveryFee)}</span> <span className="text-green-600 font-medium">Grátis</span></>
                  ) : deliveryFee === 0 ? (
                    <span className="text-green-600 font-medium">Grátis</span>
                  ) : (
                    formatCurrency(deliveryFee)
                  )}</>
                )}
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-muted-foreground">
              {feePending || taxaOculta ? 'Total parcial' : 'Total a pagar'}
            </p>
            <p className="text-2xl font-bold font-display text-primary" data-testid="checkout-total">{formatCurrency(totalTela)}</p>
            {feePending && (
              <p className="text-[11px] text-muted-foreground leading-tight">+ frete a calcular</p>
            )}
            {taxaOculta && (
              <p className="text-[11px] text-muted-foreground leading-tight">+ entrega</p>
            )}
          </div>
        </div>
        <Button
          className="w-full h-14 rounded-xl text-lg font-bold shadow-lg shadow-primary/25"
          disabled={createOrderMutation.isPending || closedAndBlocked || lojaDesativada}
          onClick={handlePlaceOrder}
        >
          {createOrderMutation.isPending ? <Loader2 className="w-6 h-6 animate-spin"/> : lojaDesativada ? "Loja não está recebendo pedidos" : closedAndBlocked ? "Loja fechada" : "Enviar Pedido por WhatsApp"}
        </Button>
      </div>
    </div>
  );
}

